<template>
  <ReviewShell
    ref="modal"
    class="payments-review-modal"
    :embedded="embedded"
    :back-label="backLabel"
    :width="reviewWidth"
    :show-footer="true"
    :confirm-close-dialog="true"
    @close="$emit('close')"
  >
    <template #title>{{ runText('paymentsReview.title') }}</template>

    <div class="review-content" :class="{ 'review-content--updating': repreviewing }">
      <!-- A check runs against the whole file, which takes seconds on a real one. The review is covered
           while it runs: the operator sees it working, and a click on rows about to be replaced cannot land. -->
      <div v-if="repreviewing" class="review-loader">
        <div class="review-loader__box">
          <BasePreloader :size="'s'" :color="'purple'" />
          <span class="body-text color--black-purple">{{ runText('paymentsReview.updating') }}</span>
        </div>
      </div>
      <!-- A cancellation changes a status and nothing else: no amount moves, no date moves, and
           the rest of the policy is untouched. Worth saying outright, because the screen is the
           settlement screen wearing different words and reads as though it might do more. -->
      <section v-if="isCancelRun" class="review-banner review-banner--cancel mb_24">
        <svg class="review-banner__icon" width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/>
          <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" stroke="currentColor" stroke-width="2"/>
        </svg>
        <div>
          <p class="body-text--bold color--black-purple mb_4">
            {{ runText('paymentsReview.cancelRun.bannerTitle') }}
          </p>
          <p class="body-text color--dark-purple">
            {{ runText('paymentsReview.cancelRun.bannerBody') }}
          </p>
        </div>
      </section>

      <!-- Everything here has been decided. A host that stays open after applying reloads what is
           still waiting, and on the last batch that comes back empty -- which used to leave the
           header, a 0% bar and two paragraphs above nothing at all. The result becomes the content:
           what the batch did, and where to change it. -->
      <section v-if="nothingLeft" class="review-done">
        <span class="review-done__glyph">✓</span>
        <p class="review-done__title">{{ runText('paymentsReview.empty.title') }}</p>
        <p v-if="notice" class="review-done__result">{{ notice }}</p>
        <p class="review-done__hint">{{ runText('paymentsReview.empty.body') }}</p>
      </section>

      <!-- What the file amounts to: the bar, and under it the same numbers in words -- each part beside its
           colour, so the text can never say something the bar does not. -->
      <section v-if="!nothingLeft && !summaryIsOneColour" class="review-summary mb_24">
        <div class="review-bar" :title="runText('paymentsReview.summary.barTitle')">
          <span class="review-bar__seg review-bar__seg--done"
                :style="{ width: summary.pct(summary.already) }"></span>
          <span class="review-bar__seg review-bar__seg--ready"
                :style="{ width: summary.pct(summary.ready) }"></span>
          <span class="review-bar__seg review-bar__seg--input"
                :style="{ width: summary.pct(summary.decide) }"></span>
          <span class="review-bar__seg review-bar__seg--off"
                :style="{ width: summary.pct(summary.untouched) }"></span>
        </div>
        <p class="review-bar__legend body-text color--dark-purple">
          <span class="review-bar__key review-bar__key--total">
            {{ runText('paymentsReview.summary.total', { count: summary.total }) }}
          </span>
          <span v-if="summary.already" class="review-bar__key">
            <i class="review-bar__swatch review-bar__swatch--done"></i>
            {{ runText('paymentsReview.summary.done', { count: summary.already }) }}
          </span>
          <span v-if="summary.ready" class="review-bar__key">
            <i class="review-bar__swatch review-bar__swatch--ready"></i>
            {{ runText('paymentsReview.summary.ready', { count: summary.ready }) }}
          </span>
          <span v-if="summary.decide" class="review-bar__key">
            <i class="review-bar__swatch review-bar__swatch--input"></i>
            {{ runText('paymentsReview.summary.input', { count: summary.decide }) }}
          </span>
          <span v-if="summary.untouched" class="review-bar__key">
            <i class="review-bar__swatch review-bar__swatch--off"></i>
            {{ runText('paymentsReview.summary.unable', { count: summary.untouched }) }}
          </span>
        </p>
      </section>

      <!-- Date format check: a format the file does not fit silently settles the wrong
           installment, so it blocks the update until the operator resolves it. -->
      <section v-if="dateFormatBlocking" class="date-format-alert mb_24">
        <p class="body-text--bold mb_8">{{ runText('paymentsReview.dateFormat.title') }}</p>
        <p class="body-text mb_8">
          {{
            runText('paymentsReview.dateFormat.body', {
              chosen: dateFormatCheck.chosen,
              column: dateFormatCheck.column,
              unparseable: dateFormatCheck.rowsUnparseable,
              checked: dateFormatCheck.rowsChecked,
            })
          }}
        </p>
        <p v-if="unparseableRowsLabel" class="body-text color--dark-purple mb_8">
          {{ runText('paymentsReview.dateFormat.rows', { rows: unparseableRowsLabel }) }}
        </p>
        <p v-if="!unparseableRowsComplete" class="body-text color--dark-purple mb_8">
          {{ runText('paymentsReview.dateFormat.listIncomplete') }}
        </p>
        <p v-if="dateFormatSkipAccepted" class="body-text color--dark-purple mb_8">
          {{ runText('paymentsReview.dateFormat.skipped', { count: skippedRowNumbers.length }) }}
        </p>
        <div class="date-format-alert__actions">
          <BaseButton
            v-if="dateFormatCheck.suggested"
            :is-small="true"
            @click="useSuggestedFormat"
          >
            {{ runText('paymentsReview.dateFormat.useSuggested', { format: dateFormatCheck.suggested }) }}
          </BaseButton>
          <BaseButton
            v-if="unparseableRowNumbers.length && unparseableRowsComplete && !dateFormatSkipAccepted"
            type="outline"
            :is-small="true"
            @click="skipUnparseableRows"
          >
            {{ runText('paymentsReview.dateFormat.skipRows', { count: unparseableRowNumbers.length }) }}
          </BaseButton>
          <div
            v-if="unparseableRowNumbers.length"
            class="body-text color--purple cursor__pointer"
            @click="downloadUnparseableRows"
          >
            {{ runText('paymentsReview.dateFormat.downloadRows') }}
          </div>
        </div>
      </section>

      <!-- The optional payment_due_date column, when what was mapped to it is not dates. One
           mapping mistake, not N bad rows: without this the operator saw every row in the file
           marked unreadable and had nothing to act on. -->
      <section v-if="dueDateMappingBroken" class="date-format-alert mb_24">
        <p class="body-text--bold mb_8">{{ runText('paymentsReview.dueDateFormat.title') }}</p>
        <p class="body-text mb_8">
          {{
            $t(dueDateColumnIsNotDates
              ? 'paymentsReview.dueDateFormat.notDates'
              : 'paymentsReview.dueDateFormat.body', {
              chosen: dueDateFormatCheck.chosen,
              column: dueDateFormatCheck.column,
              unparseable: dueDateFormatCheck.rowsUnparseable,
              checked: dueDateFormatCheck.rowsChecked,
            })
          }}
        </p>
        <p class="body-text color--dark-purple mb_8">
          {{ runText('paymentsReview.dueDateFormat.remedy') }}
        </p>
        <div class="date-format-alert__actions">
          <BaseButton :is-small="true" @click="$emit('unmapDueDate')">
            {{ runText('paymentsReview.dueDateFormat.unmap') }}
          </BaseButton>
          <BaseButton
            v-if="dueDateFormatCheck.suggested"
            type="outline"
            :is-small="true"
            @click="$emit('changeDateFormat', dueDateFormatCheck.suggested)"
          >
            {{ runText('paymentsReview.dateFormat.useSuggested', { format: dueDateFormatCheck.suggested }) }}
          </BaseButton>
        </div>
      </section>

      <!-- The meta groups: Already done, Ready to apply, Need your input -- each a heading with one line saying
           what it means, over its own groups. Unable to apply is the collapsed block further down. -->
      <template v-for="meta in metaGroups">
        <header :key="`meta-${meta.key}`" class="review-meta">
          <p class="review-meta__title">
            {{ runText(`paymentsReview.meta.${meta.key}.title`) }} ·
            <span class="review-meta__total">{{ meta.total }}</span>
          </p>
          <p class="review-meta__sub">{{ runText(`paymentsReview.meta.${meta.key}.sub`) }}</p>
        </header>
      <section
        v-for="group in meta.groups"
        :key="group.key"
        :id="`review-group-${group.key}`"
        class="review-section color--black-purple"
        :class="`review-section--${group.tone}`"
      >
        <header class="review-section__header">
          <div class="review-section__title">
            <!-- How many rows, and nothing else: what is ticked is on the checkboxes, and what will be applied
                 is on the Apply button. -->
            <span class="review-section__count">{{ group.rows.length }}</span>
            <span class="title-text">{{ group.title }}</span>
          </div>
          <div class="review-section__actions">
            <!-- Download appears once the rows are on screen. Offering a file of rows nobody has
                 looked at invites downloading instead of reading, which is the habit this screen
                 was built to break. -->
            <div v-if="expanded[group.key]" class="body-text color--purple cursor__pointer"
                 @click="download(group.key)"
                 :title="runText('paymentsReview.tooltips.downloadRows')">
              {{ runText('paymentsReview.actions.downloadAsFile') }}
            </div>
            <div class="body-text color--purple cursor__pointer" @click="toggle(group.key)">
              {{
                expanded[group.key]
                  ? runText('paymentsReview.actions.hide')
                  : runText('paymentsReview.actions.review', { count: group.rows.length })
              }}
            </div>
          </div>
        </header>
        <div v-if="group.note" class="review-section__note">
          <p class="body-text color--dark-purple">{{ group.note }}</p>
          <!-- A duplicate policy deleted in another tab frees these rows: read the file against Renova again. -->
          <BaseButton
            v-if="group.key === 'manyPolicies'"
            type="outline"
            :is-small="true"
            :loading="repreviewing"
            @click="checkAgain"
          >
            {{ runText('paymentsReview.actions.checkAgain') }}
          </BaseButton>
        </div>

        <!-- Closed until asked for, and not built until opened. The per-policy group shows policies instead --
             its rows are read a policy at a time. -->
        <PaymentsReviewRows
          v-if="expanded[group.key] && group.key !== 'manyForOnePolicy'"
          :rows="group.rows"
          :group="group"
        />

        <!-- Several rows of the file against one policy. Listed by policy rather than by row,
             because that is the unit the operator has to think in: two collections on one policy are
             one question about that policy, and reading them apart is how you settle the same month
             twice. Opening one puts its rows together, on their own. -->
        <ul v-if="expanded[group.key] && group.key === 'manyForOnePolicy'" class="policy-list">
          <li v-for="p in policyClusters" :key="p.policyId" class="policy-list__row"
              @click="openPolicy(p.policyId)">
            <span class="policy-list__number">{{ p.policyNumber || '—' }}</span>
            <span class="policy-list__insured">{{ p.insured }}</span>
            <span class="policy-list__count">
              {{ runText('paymentsReview.manyForOnePolicy.rowCount', { count: p.rows.length }) }}
            </span>
            <span v-if="p.contestedCount" class="diff-chip diff-chip--contested">
              {{ runText('paymentsReview.manyForOnePolicy.contested', { count: p.contestedCount }) }}
            </span>
            <span class="body-text color--purple policy-list__open">
              {{ runText('paymentsReview.manyForOnePolicy.open') }}
            </span>
          </li>
        </ul>

        <div class="section-footer" v-if="expanded[group.key] && hasSelection(group)">
          <BaseButton v-if="selectCounts(group).unchecked" :is-small="true" @click="selectAll(group)">
            {{ runText('paymentsReview.actions.selectAll', { count: selectCounts(group).unchecked }) }}
          </BaseButton>
          <BaseButton v-if="selectCounts(group).checked" type="outline" :is-small="true" @click="deselectAll(group)">
            {{ runText('paymentsReview.actions.deselectAll') }}
          </BaseButton>
          <!-- One click for the operator who already knows every policy here was abandoned. Still theirs. -->
          <div v-if="isCancelRun && followableRows(group).length"
               class="body-text color--purple cursor__pointer section-footer__link"
               @click="includeFollowing(group)">
            {{ runText('paymentsReview.following.all', { count: followableRows(group).length }) }}
          </div>
        </div>
      </section>
      </template>

      <!-- One policy's rows, together and alone -- as a panel over the review rather than a sheet in the
           middle of it. A policy is a detail *of* this list, not a departure from it. -->
      <BaseSideModal
        v-if="openPolicyId"
        :width="reviewWidth"
        :show-footer="true"
        @close="closePolicy"
      >
        <template #title>
          <p class="title-text color--black-purple">{{ openPolicyCluster.policyNumber }}</p>
          <p class="body-text color--dark-purple">{{ openPolicyCluster.insured }}</p>
        </template>

        <p class="body-text color--dark-purple mb_12">
          {{ runText('paymentsReview.manyForOnePolicy.note', { count: openPolicyCluster.rows.length }) }}
        </p>

        <PaymentsReviewRows :rows="openPolicyCluster.rows" :group="{}" />

        <div v-if="hasSelection(openPolicyCluster)" class="section-footer">
          <BaseButton
            v-if="selectCounts(openPolicyCluster).unchecked"
            :is-small="true"
            @click="selectAll(openPolicyCluster)"
          >
            {{ runText('paymentsReview.actions.selectAll', { count: selectCounts(openPolicyCluster).unchecked }) }}
          </BaseButton>
          <BaseButton
            v-if="selectCounts(openPolicyCluster).checked"
            type="outline"
            :is-small="true"
            @click="deselectAll(openPolicyCluster)"
          >
            {{ runText('paymentsReview.actions.deselectAll') }}
          </BaseButton>
        </div>

        <template #footer>
          <BaseButton type="outline" @click="closePolicy">
            {{ runText('paymentsReview.actions.back') }}
          </BaseButton>
        </template>
      </BaseSideModal>

      <!-- Unable to apply: collapsed behind one line until asked for, and then drawn the way every other group
           is -- the same header, the same rows, with the reason where the payment columns would be. -->
      <section v-if="untouchedRows.length">
        <header class="review-meta">
          <div class="review-meta__row">
            <p class="review-meta__title">
              {{ runText('paymentsReview.meta.off.title') }} ·
              <span class="review-meta__total">{{ untouchedRows.length }}</span>
            </p>
            <div class="body-text color--purple cursor__pointer review-meta__toggle"
                 @click="toggleQuiet('untouched')">
              {{ quietOpen.untouched ? runText('paymentsReview.actions.hide') : runText('paymentsReview.actions.seeDetail') }}
            </div>
          </div>
          <p class="review-meta__sub">{{ runText('paymentsReview.meta.off.sub') }}</p>
        </header>

        <template v-if="quietOpen.untouched">
          <section v-for="group in untouchedGroups" :key="group.key" class="review-section color--black-purple">
            <header class="review-section__header">
              <div class="review-section__title">
                <span class="review-section__count">{{ group.rows.length }}</span>
                <span class="title-text">{{ group.title }}</span>
              </div>
              <div class="review-section__actions">
                <div v-if="expanded[group.key]" class="body-text color--purple cursor__pointer"
                     @click="download(group.key)"
                     :title="runText('paymentsReview.tooltips.downloadRows')">
                  {{ runText('paymentsReview.actions.downloadAsFile') }}
                </div>
                <div class="body-text color--purple cursor__pointer" @click="toggle(group.key)">
                  {{
                    expanded[group.key]
                      ? runText('paymentsReview.actions.hide')
                      : runText('paymentsReview.actions.review', { count: group.rows.length })
                  }}
                </div>
              </div>
            </header>
            <div v-if="group.note" class="review-section__note">
              <p class="body-text color--dark-purple">{{ group.note }}</p>
            </div>
            <PaymentsReviewRows v-if="expanded[group.key]" :rows="group.rows" :group="group" mode="unable" />
          </section>
        </template>
      </section>

      <!-- Apply checked the file and some ticked rows can no longer apply. They are already unchecked and
           marked; this says so, once, instead of committing less than the button promised. -->
      <BaseDialog
        v-if="blockedDialogCount"
        :width="'444px'"
        :text-align="'left'"
        :footer-align="'flex-end'"
        :title="runText('paymentsReview.blockedDialog.title')"
        :text="runText('paymentsReview.blockedDialog.text', { count: blockedDialogCount })"
        :confirm-button-text="runText('paymentsReview.blockedDialog.review')"
        @confirm="closeBlockedDialog"
        @close="closeBlockedDialog"
      />

      <!-- How to work a table, which is noise once there is no table. The done block above says
           what happened and carries the notice itself. -->
      <p v-if="!nothingLeft" class="review-description" v-html="$t(descriptionKey)"></p>

      <!-- What the last batch did. Above the rows and it stays there: a toast
           is gone by the time you look up from the row you were reading. -->
      <p v-if="notice && !nothingLeft" class="review-notice">{{ notice }}</p>
    </div>

    <template #footer>
      <span v-if="repreviewing" class="body-text color--dark-purple review-updating">
        {{ runText('paymentsReview.updating') }}
      </span>
      <!-- Cancellation runs: what "cancel what follows" adds, in payments and money, before anyone presses. -->
      <span v-else-if="isCancelRun && followingExtra.count" class="body-text color--dark-purple review-updating">
        {{ runText('paymentsReview.following.footer', {
          total: committableRows.length + followingExtra.count,
          fromFile: committableRows.length,
          following: followingExtra.count,
          amount: formatMoney(followingExtra.amount),
        }) }}
      </span>
      <!-- Back asks before closing, exactly as a click outside does: both go through the side modal's own
           confirmation, so there is one question and one place it is asked. -->
      <BaseButton type="outline" @click="$refs.modal.closeModal()">
        {{ runText('paymentsReview.actions.back') }}
      </BaseButton>
      <!-- Apply is also the check: changed decisions are checked against the file first, and applied only
           if every ticked row still can be. -->
      <BaseButton
        @click="apply"
        :disabled="!applyCount || !dateFormatResolved || repreviewing"
        :loading="repreviewing"
        :title="dateFormatResolved ? '' : runText('paymentsReview.dateFormat.blockedTooltip')"
      >
        {{ runText(isCancelRun ? 'paymentsReview.actions.markCanceled' : 'paymentsReview.actions.applyPayments',
                   { count: applyCount + followingCount }) }}
      </BaseButton>
    </template>
  </ReviewShell>
</template>
<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import ReviewShell from '@/views/RenovaDashboard/modules/ExcelFileUploader/ReviewShell';
import {ExcelErrors} from '@/views/RenovaDashboard/modules/ExcelEditor/excel-table-utils';
import {
  PAYMENT_STATUS,
  PAYMENT_STATUSES_COLORS,
  PAYMENT_STATUSES_TEXTS,
} from '@/views/RenovaDashboard/PoliciesParts/constants';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import PaymentsReviewRows from './PaymentsReviewRows';
import { wireDecision } from './payments-review-utils';

// How many unparseable row numbers to spell out in the date-format banner before
// trimming the list with an ellipsis.
const ROW_LIST_PREVIEW_LIMIT = 20;

// Why a row cannot simply be settled, as the backend reports it in `mismatch`.
// A null reason means the row settles cleanly.
const REASONS = [
  // Not from a file at all: a carrier portal's own report, which names a last
  // payment date rather than a collection to match against a receipt. Nothing
  // was compared, so `null` -- the code for "policy, date, amount and insured
  // all matched" -- would be a claim this row cannot support.
  'CARRIER_PROPOSAL',
  'AMOUNT_DIFFERS',
  'DATE_DIFFERS',
  'DATE_AND_AMOUNT_DIFFER',
  'EARLIER_RECEIPTS_UNPAID',
  'NO_MATCHING_INSTALLMENT',
  'PAID_LESS_THAN_RECEIPT',
  'SAME_RECEIPT_AS_ANOTHER_ROW',
  'ALREADY_PAID_SAME_EVIDENCE',
  'ALREADY_PAID_NO_EVIDENCE',
  'ALREADY_PAID_OTHER_EVIDENCE',
  'POLICY_IS_INVESTMENT',
  'POLICY_HAS_NO_SCHEDULE',
  'ALREADY_SETTLED',
  'AMBIGUOUS',
  'NAME_DOES_NOT_MATCH',
  'ROW_UNREADABLE',
  'DUE_DATE_UNREADABLE',
  'NEGATIVE_AMOUNT',
  'CHOSEN_RECEIPT_GONE',
  'WILL_CREATE_RECEIPT',
  'NO_POLICY',
  'OUTSIDE_POLICY_TERM',
  'MULTIPLE_POLICIES',
  'WOULD_WRITE_OFF_PAID',
  'WRITES_OFF_PAID',
  'CANCEL_FOLLOWING_ON_PAYMENT_RUN',
];

// The four meta groups, in the order they are read: `done` (already in Renova), `ready` (exact
// matches), `input` (the operator's judgement) and `off` (not in Renova, nothing to act on).
//
// A meta group only decides which *heading* a group sits under. Whether a row applies is its own
// `willApply` flag, never this map -- so moving a group between headings changes what the screen
// says, not what the button commits.
//
// `optIn` names a group whose rows do not apply on their own but where the operator has one specific
// thing they can say, from this screen, without leaving for the policy. There is one left:
//   - `sameClient`: the policy was found by number and the holder's name does not match ours. Only a
//     person can say whether that is the same client under a different spelling.
//
// There used to be a `create` opt-in, for a collection fitting no receipt's period. It is gone with
// the backend action behind it: an import settles payments that already exist and never adds one to
// a policy's schedule.
//
// `split` is gone, with the screen it named. A collection no longer divides across receipts: it
// settles the one it is pointed at, and the operator points it with the chooser. So every group whose
// rows resolve to a policy offers the same single instrument, and there is no category of row whose
// answer lives somewhere else.
const group = (key, zone, reasons, {optIn = null} = {}) => ({
  key,
  zone,
  reasons,
  optIn,
});

const GROUPS = [
  // ALREADY DONE. Good news first: these payments are already paid, and nothing happens to them --
  // no checkbox, no chooser. The first group carries this very date and amount: a re-uploaded file
  // where everything lands there was imported before. The next two are paid too, and say what they
  // record instead -- nothing at all (a schedule bulk-imported as paid), or another collection.
  group('alreadyPaid', 'done', ['ALREADY_PAID_SAME_EVIDENCE']),
  group('alreadyPaidNoEvidence', 'done', ['ALREADY_PAID_NO_EVIDENCE']),
  group('alreadyPaidOtherEvidence', 'done', ['ALREADY_PAID_OTHER_EVIDENCE']),

  // READY TO APPLY. Exact matches only -- policy, date, amount and insured. Anything that differs,
  // however small, is under NEED YOUR INPUT even though we picked the payment and ticked the row:
  // we picked it, the operator answers for it.
  group('exact', 'ready', [null]),

  // NEED YOUR INPUT, in the design's order. Rows here are still preselected wherever their own
  // `willApply` says so; the heading only says whose judgement it is.
  group('dateDiffers', 'input', ['DATE_DIFFERS']),
  group('paidLess', 'input', ['PAID_LESS_THAN_RECEIPT']),
  group('amountDiffers', 'input', ['AMOUNT_DIFFERS']),
  group('dateAndAmount', 'input', ['DATE_AND_AMOUNT_DIFFER']),
  // Cancellation runs only: the four "details differ" groups read as one -- the operator's question is the
  // same for each (is this the right payment?), and on a cancellation the difference itself changes nothing.
  group('detailsDiffer', 'input', []),
  group('nameMismatch', 'input', ['NAME_DOES_NOT_MATCH'], {optIn: 'sameClient'}),
  // The name differs AND the date or the amount does. Its own group rather than filed under the money
  // difference, where the title named one problem and the row's chip another.
  group('nameAndDetails', 'input', [], {optIn: 'sameClient'}),
  // Cancellation runs: the payment is already PAID. The one group whose rows start unticked -- canceling
  // them writes off money that was collected, and that is the operator's call, row by row.
  group('writeOff', 'input', ['WOULD_WRITE_OFF_PAID', 'WRITES_OFF_PAID']),
  group('earlierUnpaid', 'input', ['EARLIER_RECEIPTS_UNPAID']),
  // What the operator asked to add: the only rows in the run that change a schedule's shape.
  group('willCreate', 'input', ['WILL_CREATE_RECEIPT']),
  // Several rows of the file against one policy, however each classifies. Two collections on one
  // policy are one question about that policy, and reading them apart settles a month twice.
  group('manyForOnePolicy', 'input', ['SAME_RECEIPT_AS_ANOTHER_ROW']),
  // A number more than one policy carries. Nothing to tick until the duplicates are resolved, but the
  // decision is the operator's, and each row links to every candidate policy.
  group('manyPolicies', 'input', ['MULTIPLE_POLICIES']),
  // Nothing in Renova to match, and the file holding the only payment anybody knows about: the
  // chooser opens on these with one thing to offer, which is to add it.
  group('noSchedule', 'input', ['POLICY_HAS_NO_SCHEDULE']),
  group('ambiguous', 'input', ['AMBIGUOUS']),
  group('alreadySettled', 'input', ['ALREADY_SETTLED']),
  group('noInstallment', 'input', ['NO_MATCHING_INSTALLMENT']),
  group('chosenGone', 'input', ['CHOSEN_RECEIPT_GONE']),
  // What a carrier portal proposes. Its own group because the question is not
  // "is this the right receipt" but "do you accept what the carrier says", and
  // because the answer settles every receipt up to a date rather than one.
  group('carrierProposal', 'input', ['CARRIER_PROPOSAL']),
  // A reason added on the backend after this build shipped. Placed by its own `willApply` flag.
  group('other', 'input', []),

  // UNABLE TO APPLY. Nothing on this screen can move these.
  group('noPolicy', 'off', ['NO_POLICY']),
  // The number is ours, but no policy carrying it covers the payment's date.
  group('outsideTerm', 'off', ['OUTSIDE_POLICY_TERM']),
  group('investment', 'off', ['POLICY_IS_INVESTMENT']),
  group('negativeAmount', 'off', ['NEGATIVE_AMOUNT']),
  group('unreadable', 'off', ['ROW_UNREADABLE', 'DUE_DATE_UNREADABLE']),
  group('otherUntouched', 'off', []),
];

const ZONE_OF_GROUP = new Map(GROUPS.map((g) => [g.key, g.zone]));
const GROUP_OF_REASON = new Map();
GROUPS.forEach((g) => g.reasons.forEach((r) => GROUP_OF_REASON.set(r, g.key)));

// Older backends do not send `mismatch`. Derive the same reason from the array the
// row arrived in and the error codes on it, so this screen reads the two the same way.
function inferReason(row, source) {
  if (source === 'complete') return null;
  if (source === 'multiple') return 'MULTIPLE_POLICIES';
  if (source === 'noMatch') return 'NO_POLICY';
  const codes = (row.errors || []).map((e) => e && e.code);
  const amountOff = codes.includes('PAYMENT_AMOUNT_DOES_NOT_MATCH');
  const dateOff = codes.includes('PAYMENT_DATE_DOES_NOT_MATCH');
  if (amountOff && !dateOff) return 'AMOUNT_DIFFERS';
  if (dateOff && !amountOff) return 'DATE_DIFFERS';
  // Both flagged means two different installments each match one half, and nothing
  // matches both; no flag at all means nothing on the policy lined up. Neither is
  // safe to apply, and both read as "neither field matches".
  return 'DATE_AND_AMOUNT_DIFFER';
}

function reasonOf(row, source) {
  if (Object.prototype.hasOwnProperty.call(row || {}, 'mismatch')) return row.mismatch || null;
  return inferReason(row || {}, source);
}

// The reasons a backend too old to send `willApply` would have settled. Only ever consulted when the
// flag is missing from the response: with the flag present this screen makes no judgement of its own
// about what applies, which is the point of having put it on the row.
const LEGACY_APPLICABLE = new Set(['AMOUNT_DIFFERS', 'DATE_DIFFERS', 'DATE_AND_AMOUNT_DIFFER']);

// The reasons a row applies with as it stands -- the backend's `willApply()` list. Needed for the one question
// the response cannot answer about a row the operator unchecked: would it apply if it were checked?
const APPLYING_REASONS = new Set([
  null,
  'AMOUNT_DIFFERS',
  'DATE_DIFFERS',
  'DATE_AND_AMOUNT_DIFFER',
  'EARLIER_RECEIPTS_UNPAID',
  'PAID_LESS_THAN_RECEIPT',
  'WILL_CREATE_RECEIPT',
  'WRITES_OFF_PAID',
]);
// The reasons the checkbox itself answers: checking says "same client", or "write this paid payment off".
const CONSENT_REASONS = new Set(['NAME_DOES_NOT_MATCH', 'WOULD_WRITE_OFF_PAID']);
// Done: the payment is paid, and nothing is written to it. No checkbox, never seeded, never blocked,
// and no chooser -- there is no other payment for the row to be.
const DONE_REASONS = new Set([
  'ALREADY_PAID_SAME_EVIDENCE',
  'ALREADY_PAID_NO_EVIDENCE',
  'ALREADY_PAID_OTHER_EVIDENCE',
]);

function willApplyOf(row, reason) {
  if (typeof row?.willApply === 'boolean') return row.willApply;
  return !reason || LEGACY_APPLICABLE.has(reason);
}

export default {
  name: 'PaymentsReviewModal',
  components: {
    ReviewShell,
    BaseSideModal,
    BaseButton,
    BaseDialog,
    PaymentsReviewRows,
    BasePreloader,
  },
  provide() {
    return {reviewApi: () => this};
  },
  props: {
    /** A re-preview is running: the rows on screen are about to be replaced. */
    repreviewing: {
      type: Boolean,
      default: false,
    },
    reviewData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // What to add to a zero-based data row number to name the same line in the
    // uploaded file, so the operator can find the offending cell: the header line
    // itself, plus anything above it, plus 1 for one-based numbering.
    fileRowOffset: {
      type: Number,
      default: 2,
    },
    // PAID or CANCELED. Every button on the screen says which, because "mark 76 as paid" on a
    // cancellation run is the one way this screen can misdescribe what it is about to do.
    paymentsUpdateType: {
      type: String,
      default: 'PAID',
    },
    /**
     * The opening paragraph, which is the one string on this screen that does
     * not go through runText.
     *
     * A prop because the default talks about "the file" and about downloading
     * one, and this review is no longer only reached from a file: a carrier
     * portal read arrives with the same rows and none of that vocabulary.
     */
    descriptionKey: {
      type: String,
      default: 'excelUploader.ReviewModalDescription',
    },
    /**
     * Whether a row may be re-pointed at a different receipt.
     *
     * The chooser's decisions are SETTLE/SPLIT/CREATE against an installmentId,
     * and a source whose commit cannot express those must be able to show the
     * receipts without offering a control the server will not honour. Off is
     * never the default: the file flow is built on it.
     */
    allowChooser: {
      type: Boolean,
      default: true,
    },
    /**
     * A line about what just happened, shown above the rows until it is
     * replaced. Empty for the file upload, which ends in its own summary
     * screen; a source that stays open after applying has nowhere else to say
     * it.
     */
    notice: {
      type: String,
      default: '',
    },
    /**
     * Which host this review is running in, as a key prefix.
     *
     * Empty for the file upload, whose words are the defaults; 'carrier' for a
     * portal read, which reads `paymentsReview.carrier.…` wherever such a key
     * exists and the ordinary one everywhere else. See [runText].
     */
    sourcePrefix: {
      type: String,
      default: '',
    },
    /**
     * Leave out the bar and its legend when every row is in the same one of its four parts.
     *
     * A carrier group is one kind of row by construction -- a Mark paid group is all "need your
     * input" -- so its bar is a single colour and its legend repeats the total. Off for the file
     * upload, whose file is a mix and whose bar is the first thing that says so.
     */
    hideOneColourSummary: {
      type: Boolean,
      default: false,
    },
    /**
     * Render inside the host's own drawer rather than as a second one over it.
     *
     * Off for every host that is reached from a page. On for the one reached from a list, where a
     * modal over the drawer hid the list it came from. See ReviewShell.
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    /** What the embedded breadcrumb goes back to, e.g. "All updates". */
    backLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      expanded: {},
      // Per quiet zone, keyed by its name. Both start closed: the point of collapsing them is that
      // neither needs reading to press the button.
      quietOpen: {},
      // The operator's decisions, by file row: { intent: 'APPLY' | 'SKIP', target, sameClient }. The one
      // record of what they decided. A re-check reads it and never writes it -- rebuilding decisions from
      // the backend's echo is how picks and ticks used to vanish on the next click.
      decisions: {},
      // The group each row was first shown in, by file row. A row stays there however its status
      // changes, so a row the operator has just sorted does not jump somewhere else in the list.
      placement: {},
      // Rows whose holder name disagreed with ours when first seen. Listing such a row to the backend
      // confirms the name, so it is sent as SKIP until the operator ticks it.
      nameRows: {},
      // Cancellation runs: rows whose payment was already PAID when first seen. Sent as SKIP until the
      // operator ticks one, which is them choosing to write that money off.
      writeOffRows: {},
      // The decisions the rows on screen were computed from, as sent. Apply sends exactly these; while
      // the current decisions differ -- a re-check pending, running or failed -- Apply waits.
      previewedWire: null,
      // The decisions of the re-check in flight. They become `previewedWire` when its answer lands.
      pendingWire: null,
      recheckTimer: null,
      // Rows that cannot apply as they stand, by file row, with the reason. Set when a row is first seen blocked
      // or when Apply's check finds a checked row blocked; kept until the operator points the row at another
      // payment -- so a row does not quietly come back enabled because a different row changed.
      blocked: {},
      // Apply asked for a check; its answer decides whether to apply or to show what got blocked.
      applyAfterCheck: false,
      // How many checked rows Apply's check had to uncheck -- the dialog is open while this is not zero.
      blockedDialogCount: 0,
      // The first group, in reading order, holding a row Apply's check blocked -- scrolled to once the dialog
      // is closed.
      blockedGroupKey: null,
      // The policy whose rows are open on their own, if any.
      openPolicyId: null,
      // Which row's chooser is open, by `keyOf`. One at a time: each panel renders a whole policy's
      // schedule, and a screen with forty of them open is the screen this replaced.
      choosingKey: null,
      // The chooser's split while it is being ticked: the payment ids, or null when the chooser is
      // picking one payment. Committed to the row only by "Use these payments".
      splitDraft: null,
      // Rows the operator chose to leave out of this import because their date
      // could not be read in the chosen format. Zero-based, matching rowNumber.
      skippedRowNumbers: [],
      dateFormatSkipAccepted: false,
    };
  },
  computed: {
    // Every row of the response, decorated with its reason, its zone and whether it applies. The
    // response splits rows by how the match was found; this screen splits them by what will happen
    // to them, which is what the operator is deciding.
    rows() {
      const out = [];
      [
        ['complete', this.reviewData?.completeMatches],
        ['partial', this.reviewData?.partialMatches],
        ['noMatch', this.reviewData?.noMatches],
      ].forEach(([source, rows]) => {
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          if (!row) return;
          out.push(this.decorate(row, reasonOf(row, source)));
        });
      });
      // A row several policies could belong to arrives as a group of candidates. It
      // is one decision, so it gets one line — and no policy or client link, because
      // naming one of the candidates would pass a guess off as the answer.
      (Array.isArray(this.reviewData?.multipleMatches) ? this.reviewData.multipleMatches : [])
        .forEach((group) => {
          const candidates = (Array.isArray(group) ? group : []).filter(Boolean);
          if (!candidates.length) return;
          const [first] = candidates;
          const reason = reasonOf(first, 'multiple') || 'MULTIPLE_POLICIES';
          out.push(this.decorate({
            ...first,
            policyId: null,
            personProfileId: null,
            clientCompanyId: null,
            fullName: '',
            policyNumber: first.csvPolicyNumber || first.policyNumber,
            candidateCount: candidates.length,
            candidates: candidates.map((c) => ({
              policyId: c.policyId,
              policyNumber: c.policyNumber,
              fullName: c.fullName,
            })),
          }, reason));
        });
      // Belonging to a policy this file mentions more than once outranks whatever the row is on its own --
      // reading those rows apart is the mistake the group exists to stop. But only for rows still waiting
      // on a decision: a row already recorded is done, and one we cannot touch is not in Renova. Pulled in,
      // they showed as "already done" under Need your input. A policy left with one open row is not
      // several, so that row stays in its own group.
      const openByPolicy = new Map();
      out.forEach((row) => {
        if (this.clustersWithPolicy(row)) openByPolicy.set(row.policyId, (openByPolicy.get(row.policyId) || 0) + 1);
      });
      out.forEach((row, i) => {
        if (this.clustersWithPolicy(row) && openByPolicy.get(row.policyId) >= 2) {
          out[i] = {...row, groupKey: 'manyForOnePolicy'};
        }
      });
      // A row stays in the group it was first shown in. Its status changes with every re-check; where
      // the operator found it does not.
      return out.map((row) => {
        const placed = Number.isInteger(row.rowNumber) ? this.placement[row.rowNumber] : null;
        return placed && placed !== row.groupKey ? {...row, groupKey: placed} : row;
      });
    },
    groupsByKey() {
      const out = {};
      GROUPS.forEach((g) => {
        out[g.key] = {...g, rows: []};
      });
      this.rows.forEach((row) => out[row.groupKey].rows.push(row));
      // Rows of one policy sit together. A carrier's file lists collections by date, so three rows for
      // one policy can arrive forty lines apart, and deciding about them means holding all three in your
      // head at once.
      //
      // Sorted by policy, then by the row's own place in the file -- so the order *within* a policy is
      // still the order the carrier sent, and a row never moves for a reason the operator cannot see.
      Object.values(out).forEach((group) => {
        group.rows.sort((a, b) => {
          const byPolicy = this.policyKeyOf(a).localeCompare(this.policyKeyOf(b));
          return byPolicy !== 0 ? byPolicy : (a.rowNumber ?? 0) - (b.rowNumber ?? 0);
        });
      });
      return out;
    },
    // Every group with rows in it, in the order they are worth reading, flat. `tone` is what the
    // zone used to be -- it still decides the accent, and still decides which rows count as ready
    // -- but it is no longer a heading with groups nested under it.
    orderedGroups() {
      return GROUPS
        .filter((g) => g.zone !== 'off')
        .map((g) => ({
          ...this.groupsByKey[g.key],
          tone: g.zone,
          optIn: g.optIn,
          title: this.runText(`paymentsReview.groups.${g.key}`),
          note: this.groupNote(g.key),
        }))
        .filter((g) => g.rows.length);
    },
    // Nothing to show at all -- not "nothing selected" or "nothing applicable", but no rows. Both
    // lists, because "unable to apply" is still something to look at.
    nothingLeft() {
      return !this.rows.length && !this.untouchedRows.length;
    },
    // The headings over the groups, in reading order. Unable to apply is its own collapsed block, so it is not
    // listed here.
    metaGroups() {
      return ['done', 'ready', 'input']
        .map((key) => {
          const groups = this.orderedGroups.filter((g) => g.tone === key);
          return {key, groups, total: groups.reduce((n, g) => n + g.rows.length, 0)};
        })
        .filter((m) => m.total);
    },
    // The one bucket still collapsed behind a single line: rows this screen cannot act on. Spelling
    // out six tables of them is what made the old screen unreadable.
    untouchedGroups() {
      return GROUPS
        .filter((g) => g.zone === 'off')
        .map((g) => ({
          ...this.groupsByKey[g.key],
          title: this.runText(`paymentsReview.groups.${g.key}`),
          note: this.groupNote(g.key),
        }))
        .filter((g) => g.rows.length);
    },
    untouchedRows() {
      return this.untouchedGroups.reduce((acc, g) => acc.concat(g.rows), []);
    },
    // The bar's segments and the line under it -- one set of numbers, so the words and the colours agree.
    /** See [hideOneColourSummary]: at most one of the bar's four parts has anything in it. */
    summaryIsOneColour() {
      if (!this.hideOneColourSummary) return false;
      const s = this.summary;
      return [s.already, s.ready, s.decide, s.untouched].filter((n) => n > 0).length <= 1;
    },
    summary() {
      const rowsIn = (tone) => this.orderedGroups
        .filter((g) => g.tone === tone)
        .reduce((acc, g) => acc + g.rows.length, 0);
      const ready = rowsIn('ready');
      const decide = rowsIn('input');
      const already = rowsIn('done');
      const untouched = this.untouchedRows.length;
      const total = ready + decide + already + untouched;
      return {
        ready,
        decide,
        already,
        untouched,
        total,
        // Guarded against a zero total so an empty review does not divide by it.
        pct: (n) => (total ? `${(n / total) * 100}%` : '0%'),
      };
    },
    // Policies this file has more than one row against, as the backend counted them -- it sees the
    // whole file, including rows this screen files under "we can't touch these".
    multiRowPolicyIds() {
      return new Set((this.reviewData?.policyRowGroups || []).map((g) => g.policyId));
    },
    // Those policies with their rows attached, in policy-number order.
    policyClusters() {
      const byPolicy = new Map();
      (this.groupsByKey.manyForOnePolicy?.rows || []).forEach((row) => {
        if (!byPolicy.has(row.policyId)) {
          byPolicy.set(row.policyId, {
            key: 'manyForOnePolicy',
            policyId: row.policyId,
            policyNumber: row.policyNumber || row.csvPolicyNumber,
            insured: row.fullName,
            contestedCount: 0,
            rows: [],
          });
        }
        const cluster = byPolicy.get(row.policyId);
        cluster.rows.push(row);
        if (row.mismatchReason === 'SAME_RECEIPT_AS_ANOTHER_ROW') cluster.contestedCount += 1;
      });
      return [...byPolicy.values()]
        .sort((a, b) => String(a.policyNumber).localeCompare(String(b.policyNumber)));
    },
    openPolicyCluster() {
      return this.policyClusters.find((c) => c.policyId === this.openPolicyId)
        || {
          rows: [], policyNumber: '', insured: '', key: 'manyForOnePolicy'
        };
    },
    // The schedules the chooser renders, by policy. Absent on an older backend, in which case the
    // chooser simply does not open -- the rest of the screen is unaffected.
    schedulesByPolicy() {
      return this.reviewData?.schedules || {};
    },
    // A cancellation run says so on every button. The footer used to read "Mark N as paid" while
    // cancelling, which is the one place the screen could mislead about what it was about to do.
    isCancelRun() {
      return this.paymentsUpdateType === 'CANCELED';
    },
    chooserRow() {
      if (!this.choosingKey) return null;
      return this.rows.find((r) => this.keyOf(r) === this.choosingKey) || null;
    },
    // The whole schedule, in due-date order -- the rows the policy page shows, so the two can be read
    // side by side. Settled ones are there to be recognised, not picked: `canPick` greys them out.
    chooserReceipts() {
      const row = this.chooserRow;
      if (!row) return [];
      return (this.schedulesByPolicy[row.policyId]?.receipts || [])
        .slice()
        .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));
    },
    chooserHasUnpaid() {
      return this.chooserReceipts.some((r) => r.status === 'UNPAID');
    },
    // Absent on older backends — everything below then reads as "nothing to check".
    dateFormatCheck() {
      return this.reviewData?.dateFormatCheck || null;
    },
    dateFormatBlocking() {
      return !!this.dateFormatCheck && this.dateFormatCheck.ok === false;
    },
    dueDateFormatCheck() {
      return this.reviewData?.dueDateFormatCheck || null;
    },
    dueDateMappingBroken() {
      return !!this.dueDateFormatCheck && this.dueDateFormatCheck.ok === false;
    },
    // Every value failed, so this is not a format reading the column badly -- it is a column holding
    // something other than dates. Worth saying, because the two have different fixes.
    dueDateColumnIsNotDates() {
      const c = this.dueDateFormatCheck;
      return !!c && c.rowsChecked > 0 && c.rowsUnparseable >= c.rowsChecked;
    },
    unparseableRowNumbers() {
      const rows = this.dateFormatCheck?.unparseableRowNumbers;
      return Array.isArray(rows) ? rows.filter((n) => Number.isInteger(n)) : [];
    },
    // Rows can only be skipped when we know every one of them. A sample would leave
    // unreadable dates in the commit, which is the very thing this check exists to stop.
    unparseableRowsComplete() {
      const total = this.dateFormatCheck?.rowsUnparseable;
      if (!Number.isInteger(total)) return this.unparseableRowNumbers.length > 0;
      return this.unparseableRowNumbers.length >= total;
    },
    unparseableRowsLabel() {
      const shown = this.unparseableRowNumbers
        .slice(0, ROW_LIST_PREVIEW_LIMIT)
        .map((n) => n + this.fileRowOffset);
      if (!shown.length) return '';
      const suffix = this.unparseableRowNumbers.length > shown.length ? ', …' : '';
      return `${shown.join(', ')}${suffix}`;
    },
    // A blocking check stays blocking until the operator either switches format
    // (which reloads this modal with a fresh response) or skips the offending rows.
    dateFormatResolved() {
      return !this.dateFormatBlocking || this.dateFormatSkipAccepted;
    },
    // Rows the last re-check says will be written. The backend has already applied every decision --
    // skips included -- so this is its answer, not a second tally kept here.
    committableRows() {
      return this.rows.filter((row) => row.willApplyRow && !this.isSkipped(row));
    },
    // What the Apply button counts: rows checked and free to apply, as the operator has them now. Apply checks
    // them against the file before writing, so this is never a promise the commit cannot keep.
    applyCount() {
      return this.rows.filter((row) => this.isChecked(row)).length;
    },
    // Cancellation runs: the payments "cancel what follows" takes with the checked rows. The button counts them
    // too -- they are canceled by the same click.
    followingCount() {
      if (!this.isCancelRun) return 0;
      return this.rows
        .filter((row) => this.isChecked(row) && this.canFollow(row) && this.followsOn(row))
        .reduce((n, row) => n + this.followingOf(row).count, 0);
    },
    // As wide as the screen allows, up to a width where every column fits without scrolling.
    reviewWidth() {
      return 'min(1760px, calc(100vw - 32px))';
    },
    rowByNumber() {
      const map = new Map();
      this.rows.forEach((row) => {
        if (Number.isInteger(row.rowNumber) && !map.has(row.rowNumber)) map.set(row.rowNumber, row);
      });
      return map;
    },
    // What goes to both endpoints: every row with a policy, from the operator's decisions.
    wire() {
      const out = [];
      const seen = new Set();
      this.rows.forEach((row) => {
        if (!row.policyId || !Number.isInteger(row.rowNumber) || this.isSkipped(row)) return;
        if (seen.has(row.rowNumber)) return;
        seen.add(row.rowNumber);
        const decision = this.decisions[row.rowNumber];
        const blocked = (!!this.nameRows[row.rowNumber] && !decision?.sameClient)
          || (!!this.writeOffRows[row.rowNumber] && !decision?.writeOff);
        out.push(wireDecision(row, decision, blocked));
      });
      return out;
    },
    wireKey() {
      return JSON.stringify(this.wire);
    },
    // Cancellation runs: payments "cancel what follows" adds on top of the rows that apply.
    followingExtra() {
      if (!this.isCancelRun) return {count: 0, amount: 0};
      return this.committableRows.reduce((acc, row) => {
        if (!this.followsOn(row)) return acc;
        const following = this.followingOf(row);
        return {count: acc.count + following.count, amount: acc.amount + following.amount};
      }, {count: 0, amount: 0});
    },
  },
  created() {
    this.rememberRows();
    const seededMore = this.seedDecisions();
    // The first review was computed with no decisions at all. Where the seed says only that -- our
    // suggestions, applied where they apply -- the rows on screen are already its answer. Where it says
    // more (a cancellation creating what does not exist), they are not, and a re-check runs straight away.
    this.previewedWire = seededMore ? '' : this.wireKey;
    if (seededMore) this.recheckNow();
  },
  beforeDestroy() {
    clearTimeout(this.recheckTimer);
  },
  watch: {
    // A re-check answered. Nothing the operator decided is touched -- not their decisions, not the
    // unreadable rows they skipped, not the sections they opened. The rows are simply newer. A new file
    // or a new date format remounts this screen instead, and starts again from our suggestions.
    reviewData() {
      this.rememberRows();
      if (this.pendingWire !== null) {
        this.previewedWire = this.pendingWire;
        this.pendingWire = null;
      }
      if (this.applyAfterCheck) {
        this.applyAfterCheck = false;
        this.finishApplyCheck();
      }
    },
    // A check that failed leaves nothing to take over: forget it, so the next Apply checks again. Deferred a
    // tick, because a check that succeeded clears `pendingWire` in the watcher above first.
    repreviewing(now) {
      if (now) return;
      this.$nextTick(() => {
        if (this.pendingWire === null) return;
        this.pendingWire = null;
        this.applyAfterCheck = false;
      });
    },
  },
  methods: {
    // What counts as "the same policy" for clustering. The number when we have one -- that is what the
    // operator reads -- and the file's spelling otherwise, so rows that matched nothing still group by
    // the number the carrier wrote rather than scattering.
    policyKeyOf(row) {
      return String(row?.policyNumber || row?.csvPolicyNumber || '');
    },
    // A row that joins its policy's other rows under "Several payments for one policy": the file has more
    // than one row against the policy, and this row still needs the operator (ready or input, not done/off).
    clustersWithPolicy(row) {
      const zone = ZONE_OF_GROUP.get(row.groupKey);
      return !!row.policyId && this.multiRowPolicyIds.has(row.policyId) && (zone === 'ready' || zone === 'input');
    },
    decorate(row, reason) {
      const applies = willApplyOf(row, reason);
      const known = GROUP_OF_REASON.get(reason === undefined ? null : reason);
      // An unknown reason is placed by the flag, never by a guess about what the reason means.
      let groupKey = known || (applies ? 'other' : 'otherUntouched');
      // A name mismatch with a date or amount difference as well is two questions about one row, and it gets
      // a group that says both. Filed under the money difference instead, its title named one problem and its
      // chip the other.
      if (reason === 'NAME_DOES_NOT_MATCH' && (this.amountApart(row) || this.daysApart(row))) {
        groupKey = 'nameAndDetails';
      }
      // A cancel run matches by due date and writes the file's amount either way, so a date and an amount
      // difference are one question there.
      if (this.isCancelRun && ['dateDiffers', 'amountDiffers', 'paidLess', 'dateAndAmount'].includes(groupKey)) {
        groupKey = 'detailsDiffer';
      }
      return {
        ...row,
        mismatchReason: reason,
        willApplyRow: applies,
        groupKey,
      };
    },
    // A group whose heading needs a sentence under it. The rest say enough in the heading, and a
    // paragraph over every table is what made the old screen impossible to skim.
    groupNote(key) {
      const withNotes = ['exact', 'alreadyPaid', 'alreadyPaidNoEvidence', 'alreadyPaidOtherEvidence', 'dateDiffers', 'amountDiffers', 'willCreate',
        'earlierUnpaid', 'paidLess', 'manyForOnePolicy', 'ambiguous', 'alreadySettled',
        'noInstallment', 'chosenGone', 'nameMismatch', 'negativeAmount', 'noPolicy',
        'noSchedule', 'manyPolicies', 'detailsDiffer', 'writeOff', 'nameAndDetails', 'outsideTerm'];
      return withNotes.includes(key) ? this.runText(`paymentsReview.groupNotes.${key}`) : '';
    },
    // Guarded the way reasonOf guards the same array: an element without `details` used to throw
    // inside the render, taking the whole review down rather than the one cell.
    tooltipError(row) {
      const rowErrors = (row?.errors || []).filter((error) => error && error.code);
      if (!rowErrors.length) return '';
      return rowErrors.map((error) => {
        const details = error.details || {};
        const text = ExcelErrors[error.code] ? this.$t(ExcelErrors[error.code], details) : 'Unknown error';
        return details.fieldName ? `${details.fieldName}: ${text}` : text;
      }).join(';<br><br> ');
    },
    // First sight of a file. Every row that can apply starts checked -- a name that differs too, because the
    // operator sees it and unchecks a wrong guess. A paid payment a cancellation would write off starts
    // unchecked. A row that cannot apply as it stands starts unchecked and blocked; of two lines on one
    // payment, the first keeps it. Runs once -- the decisions are the operator's from here on.
    seedDecisions() {
      const next = {};
      const blocked = {};
      let seededMore = false;
      this.rows.forEach((row) => {
        if (!row.policyId || !Number.isInteger(row.rowNumber)) return;
        if (DONE_REASONS.has(row.mismatchReason)) return;
        // Cancellation runs: a policy with no payments yet starts as "create it from this row and cancel it"
        // -- the carrier says the charge is withdrawn, and leaving no trace would break the reconciliation.
        if (this.isCancelRun && row.mismatchReason === 'POLICY_HAS_NO_SCHEDULE') {
          next[row.rowNumber] = {intent: 'APPLY', target: {create: true}};
          seededMore = true;
          return;
        }
        const reason = this.blockReasonNow(row);
        if (reason) {
          next[row.rowNumber] = {intent: 'SKIP'};
          blocked[row.rowNumber] = reason;
          // Skipping the later line of a pair frees the payment for the first, which the check has to see.
          if (row.mismatchReason === 'SAME_RECEIPT_AS_ANOTHER_ROW') seededMore = true;
          return;
        }
        if (row.mismatchReason === 'WOULD_WRITE_OFF_PAID') {
          next[row.rowNumber] = {intent: 'SKIP'};
          return;
        }
        if (row.mismatchReason === 'NAME_DOES_NOT_MATCH') {
          next[row.rowNumber] = {intent: 'APPLY', sameClient: true};
          seededMore = true;
          return;
        }
        next[row.rowNumber] = {intent: 'APPLY'};
      });
      this.decisions = next;
      this.blocked = blocked;
      return seededMore;
    },
    // Where each new row first appears, and whether its holder name disagreed. Only rows not seen
    // before are recorded, so neither changes once the operator has seen the row.
    rememberRows() {
      let placement = null;
      let nameRows = null;
      let writeOffRows = null;
      this.rows.forEach((row) => {
        if (!Number.isInteger(row.rowNumber)) return;
        if (!this.placement[row.rowNumber]) {
          placement = placement || {...this.placement};
          placement[row.rowNumber] = row.groupKey;
        }
        if (row.mismatchReason === 'NAME_DOES_NOT_MATCH' && !this.nameRows[row.rowNumber]) {
          nameRows = nameRows || {...this.nameRows};
          nameRows[row.rowNumber] = true;
        }
        if (['WOULD_WRITE_OFF_PAID', 'WRITES_OFF_PAID'].includes(row.mismatchReason) && !this.writeOffRows[row.rowNumber]) {
          writeOffRows = writeOffRows || {...this.writeOffRows};
          writeOffRows[row.rowNumber] = true;
        }
      });
      if (placement) this.placement = placement;
      if (nameRows) this.nameRows = nameRows;
      if (writeOffRows) this.writeOffRows = writeOffRows;
    },
    decisionOf(row) {
      return (row && Number.isInteger(row.rowNumber) && this.decisions[row.rowNumber]) || {};
    },
    // The only way a decision changes: merged into the named rows' entries, never another row's.
    decide(rowNumbers, patch) {
      const next = {...this.decisions};
      rowNumbers.forEach((n) => {
        next[n] = {...(next[n] || {}), ...patch};
      });
      this.decisions = next;
    },
    recheckNow() {
      clearTimeout(this.recheckTimer);
      this.recheckTimer = null;
      this.pendingWire = this.wireKey;
      this.$emit('repreview', {rowDecisions: this.wire});
    },
    toggle(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    toggleQuiet(key) {
      this.$set(this.quietOpen, key, !this.quietOpen[key]);
    },
    // The policy page's own filters, so a payment reads identically here, in the chooser and on the
    // policy: "4 423,2" and "12.08.2026", not the browser's locale and a raw ISO string.
    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '—';
      return this.$options.filters.formatAmount(val);
    },
    formatDate(val) {
      return val ? this.$options.filters.formatDate(val, true) : '—';
    },
    isSkipped(row) {
      return Number.isInteger(row?.rowNumber) && this.skippedRowNumbers.includes(row.rowNumber);
    },
    // Every row with a policy has a decision the operator can hold -- a blocked one included, which Change
    // payment can free.
    rowIsSelectable(row) {
      return !!(row && row.policyId && Number.isInteger(row.rowNumber) && !this.isSkipped(row));
    },
    selectableRows(group) {
      return (group.rows || []).filter((row) => this.rowIsSelectable(row));
    },
    // Why a row cannot apply as it stands, from the last check -- '' when it can, or when checking it is itself
    // the answer (a name to confirm, a paid payment to write off). Of two lines on one payment, the first keeps
    // it and the later ones name it.
    blockReasonNow(row) {
      if (!row || !row.policyId) return '';
      const reason = row.mismatchReason === undefined ? null : row.mismatchReason;
      if (DONE_REASONS.has(reason)) return '';
      if (reason === 'SAME_RECEIPT_AS_ANOTHER_ROW') {
        const earlier = (row.contestedWithRows || []).filter((n) => n < row.rowNumber);
        if (!earlier.length) return '';
        return this.runText('paymentsReview.blocked.samePaymentAs', {
          lines: earlier.map((n) => n + this.fileRowOffset).join(', '),
        });
      }
      if (APPLYING_REASONS.has(reason) || CONSENT_REASONS.has(reason) || row.willApplyRow) return '';
      return this.statusText(row);
    },
    // The reason shown on a disabled checkbox. A number several policies carry is blocked until one is left.
    blockedText(row) {
      // The source's own sentence, when it has one, ahead of everything else -- including the
      // selectable test, which a row it blocked cannot pass. A carrier read decides server-side
      // whether a row can apply and says why; without this the row arrived with no checkbox and
      // nothing at all to explain the absence.
      if (row?.blockedNote) return row.blockedNote;
      if (row?.candidates && row.candidates.length) return this.statusText(row);
      if (!this.rowIsSelectable(row)) return '';
      return this.blocked[row.rowNumber] || this.blockReasonNow(row);
    },
    isBlocked(row) {
      return !!this.blockedText(row);
    },
    // A row with something to apply shows a checkbox. Already done has nothing to apply.
    showsCheckbox(row) {
      if (row?.candidates && row.candidates.length) return true;
      return this.rowIsSelectable(row) && !DONE_REASONS.has(row.mismatchReason);
    },
    isChecked(row) {
      return this.showsCheckbox(row) && !this.isBlocked(row) && this.isSelected(row);
    },
    reviewRowClass(row) {
      return this.isBlocked(row) ? 'review-row--blocked' : '';
    },
    // What a group's Select all / Deselect all would change. Blocked rows are never counted or touched.
    selectCounts(group) {
      const rows = (group.rows || []).filter((row) => this.showsCheckbox(row) && !this.isBlocked(row));
      const checked = rows.filter((row) => this.isChecked(row)).length;
      return {checked, unchecked: rows.length - checked};
    },
    hasSelection(group) {
      const counts = this.selectCounts(group);
      return counts.checked > 0 || counts.unchecked > 0;
    },
    selectAll(group) {
      (group.rows || [])
        .filter((row) => this.showsCheckbox(row) && !this.isBlocked(row) && !this.isChecked(row))
        .forEach((row) => this.decide([row.rowNumber], {intent: 'APPLY', ...this.consentOf(row)}));
    },
    deselectAll(group) {
      const rowNumbers = (group.rows || []).filter((row) => this.isChecked(row)).map((row) => row.rowNumber);
      if (rowNumbers.length) this.decide(rowNumbers, {intent: 'SKIP'});
    },
    unblock(row) {
      if (row && Number.isInteger(row.rowNumber) && this.blocked[row.rowNumber]) {
        this.$delete(this.blocked, row.rowNumber);
      }
    },
    // Apply is the check. Decisions the rows on screen were computed from are applied straight away; changed
    // ones are checked against the file first.
    apply() {
      if (this.repreviewing || !this.dateFormatResolved || !this.applyCount) return;
      if (this.pendingWire === null && this.previewedWire === this.wireKey) {
        this.emitUpdate();
        return;
      }
      this.applyAfterCheck = true;
      this.recheckNow();
    },
    // The check answered. A checked row that can no longer apply is unchecked and marked blocked, the dialog
    // says how many, and nothing is written -- the next Apply writes what is checked then.
    finishApplyCheck() {
      const newlyBlocked = this.rows.filter((row) => this.rowIsSelectable(row)
        && this.decisionOf(row).intent !== 'SKIP'
        && !this.blocked[row.rowNumber]
        && this.blockReasonNow(row));
      if (!newlyBlocked.length) {
        this.emitUpdate();
        return;
      }
      const blocked = {...this.blocked};
      newlyBlocked.forEach((row) => {
        blocked[row.rowNumber] = this.blockReasonNow(row);
      });
      this.blocked = blocked;
      this.decide(newlyBlocked.map((row) => row.rowNumber), {intent: 'SKIP'});
      // Open every group holding one of them, so they are on screen rather than behind a "Review" link.
      const groupKeys = new Set(newlyBlocked.map((row) => row.groupKey));
      groupKeys.forEach((key) => this.$set(this.expanded, key, true));
      this.blockedGroupKey = GROUPS.map((g) => g.key).find((key) => groupKeys.has(key)) || null;
      this.blockedDialogCount = newlyBlocked.length;
    },
    // The dialog read, the review goes to the first of those groups.
    closeBlockedDialog() {
      this.blockedDialogCount = 0;
      const key = this.blockedGroupKey;
      this.blockedGroupKey = null;
      if (!key) return;
      this.$nextTick(() => {
        const section = document.getElementById(`review-group-${key}`);
        if (section) section.scrollIntoView({behavior: 'smooth', block: 'start'});
      });
    },
    // "Check again": the file read against Renova as it is now. A duplicate policy deleted in another tab frees
    // its rows here, and they stay in the group they were found in.
    checkAgain() {
      if (this.repreviewing) return;
      this.recheckNow();
    },
    // The holder the row is about. A number several policies carry: their holders, else the file's name.
    insuredName(row) {
      if (row?.candidates && row.candidates.length) {
        const names = [...new Set(row.candidates.map((c) => c.fullName).filter(Boolean))];
        return names.join(' / ') || row.csvFullName || '—';
      }
      return row?.fullName || row?.csvFullName || '—';
    },
    // The holder name disagreed when the row was first seen -- still true after the operator confirmed it.
    nameDiffers(row) {
      return !!(row && Number.isInteger(row.rowNumber) && this.nameRows[row.rowNumber] && row.csvFullName);
    },
    // Will this field actually move when the button is pressed? Two conditions, and both matter: the
    // values have to differ, and the row has to be one that applies. A row in "you decide" differs
    // too, and nothing is going to happen to it yet.
    // -- the two sides, and what differs between them ----------------------------------------
    //
    // The payment as it stands: amount then date, the same shape as the file's side, so the eye can
    // compare them without the row explaining itself. Empty when no payment is on the row yet.
    // Which half of a side is the one that moved. The DIFFERENCE chip says how much, but the eye
    // still needs to know *which* number it is looking at -- and this is what the redesign dropped:
    // the two sides became plain strings, so a row with a date difference and a row with an amount
    // difference looked identical until you read the chip and then went back.
    amountMoved(row) {
      return !!row.installmentId && !!this.amountApart(row);
    },
    dateMoved(row) {
      return !!row.installmentId && !!this.daysApart(row);
    },
    // Null when the row is not settling anything, so the cell falls back to a single sentence.
    renovaNote(row) {
      if (row.mismatchReason === 'WILL_CREATE_RECEIPT') {
        return this.runText('paymentsReview.difference.newPayment');
      }
      // Not "none chosen" -- there is nothing to choose. The policy carries no payments at all.
      if (row.mismatchReason === 'POLICY_HAS_NO_SCHEDULE') {
        return this.runText('paymentsReview.difference.noSchedule');
      }
      if (!row.installmentId) return this.runText('paymentsReview.difference.noPayment');
      return null;
    },
    // The payment the operator pointed the row at, when the re-check did not settle it -- so a refused
    // pick is still on the row, next to the reason it was refused.
    pickedLabel(row) {
      const target = this.decisionOf(row).target || {};
      if (!target.installmentId || target.installmentId === row.installmentId) return '';
      const receipt = (this.schedulesByPolicy[row.policyId]?.receipts || [])
        .find((r) => r.id === target.installmentId);
      if (!receipt) return this.runText('paymentsReview.picked.gone');
      return this.runText('paymentsReview.picked.payment', {
        date: this.formatDate(receipt.dueDate),
        amount: this.formatMoney(receipt.amount),
      });
    },
    // Days between the payment's due date and the day the money arrived. Null when either is
    // missing, so a row with nothing to compare says nothing rather than guessing at zero.
    daysApart(row) {
      const due = this.parseDate(row.paymentDate);
      const paid = this.parseDate(row.csvPaymentDate);
      if (!due || !paid) return null;
      return Math.round((paid - due) / 86400000);
    },
    parseDate(val) {
      if (!val) return null;
      const t = Date.parse(val);
      return Number.isNaN(t) ? null : t;
    },
    amountApart(row) {
      const ours = Number(row.paymentAmount);
      const theirs = Number(row.csvPaymentAmount);
      if (Number.isNaN(ours) || Number.isNaN(theirs)) return null;
      const d = theirs - ours;
      // A cent either way is rounding, not a difference worth a chip.
      return Math.abs(d) < 0.005 ? 0 : d;
    },
    /**
     * What differs, and only what differs.
     *
     * This column used to be the row's whole status -- the reason, the shortfall, and every unpaid
     * payment behind it, in a sentence. Which meant the one number the operator was actually
     * checking sat somewhere inside a paragraph they had to read to the end to find. Now the row
     * says the difference and nothing else; the debts live in the chooser, where they are the thing
     * being decided rather than a footnote to a row that is already correct.
     */
    differenceParts(row) {
      // A number several policies carry: there is no payment to compare until one policy is left.
      if (row.candidates && row.candidates.length) return [];
      // Two or more rows of this file on one payment: named before any amount or date difference, because it
      // is the one the operator has to resolve first -- and it names the other rows.
      if (row.contestedWithRows && row.contestedWithRows.length) {
        return [this.runText('paymentsReview.difference.samePaymentAs', {
          rows: row.contestedWithRows.map((n) => n + this.fileRowOffset).join(', '),
        })];
      }
      // Nothing to be different from: this row is not settling a payment, it is adding one.
      if (row.mismatchReason === 'WILL_CREATE_RECEIPT') {
        return [this.runText('paymentsReview.difference.willAdd')];
      }
      if (row.mismatchReason === 'POLICY_HAS_NO_SCHEDULE') {
        return [this.runText('paymentsReview.difference.noSchedule')];
      }
      const parts = [];
      // The name leads when it differs, and every other difference follows it.
      if (this.nameDiffers(row)) parts.push(this.runText('paymentsReview.difference.nameDiffers'));
      // Nothing was matched, so there is nothing to be different from. That IS the difference.
      if (!row.installmentId) {
        return parts.length ? parts : [this.runText('paymentsReview.difference.noPayment')];
      }
      const amount = this.amountApart(row);
      const days = this.daysApart(row);
      if (amount) {
        parts.push((amount > 0 ? '+' : '−') + this.formatMoney(Math.abs(amount)));
      }
      if (days) {
        parts.push(this.runText(
          days > 0 ? 'paymentsReview.difference.daysLate' : 'paymentsReview.difference.daysEarly',
          {days: Math.abs(days)},
        ));
      }
      return parts;
    },
    // Money that did not arrive is coloured apart from money that arrived late or over. It is the
    // one difference that leaves a debt behind, and the only one worth interrupting a scan for.
    differenceClass(row) {
      if (row.contestedWithRows && row.contestedWithRows.length) return 'diff-chip--contested';
      if (row.mismatchReason === 'WILL_CREATE_RECEIPT') return 'diff-chip--add';
      if (row.mismatchReason === 'POLICY_HAS_NO_SCHEDULE') return 'diff-chip--open';
      if (row.mismatchReason === 'NAME_DOES_NOT_MATCH') return 'diff-chip--neutral';
      if (!row.installmentId) return 'diff-chip--open';
      const amount = this.amountApart(row);
      return amount < 0 ? 'diff-chip--short' : 'diff-chip--neutral';
    },
    // Whose choice the row's payment is. Rendered beside the Change button, because it answers the
    // question that button raises -- "change from what?" -- where the operator is already looking.
    rowStateOf(row) {
      if (!this.rowIsSelectable(row) || this.isBlocked(row)) return '';
      // Already done: nobody suggested or picked anything, the payment is simply paid.
      if (DONE_REASONS.has(row.mismatchReason)) return '';
      const target = this.decisionOf(row).target || {};
      if (target.create) return 'create';
      if (target.installmentId || (Array.isArray(target.installmentIds) && target.installmentIds.length >= 2)) {
        return 'chosen';
      }
      return row.installmentId ? 'suggested' : '';
    },
    willChange(row, field) {
      if (!row?.willApplyRow || this.isSkipped(row)) return false;
      if (field === 'amount') {
        const ours = row.paymentAmount;
        const theirs = row.csvPaymentAmount;
        if (ours === null || ours === undefined || ours === '') return false;
        return Number(ours) !== Number(theirs);
      }
      // Dates arrive as plain yyyy-MM-dd strings on both sides; a missing one is not a difference to
      // paint, it is a row with nothing matched.
      const ourDate = (row.paymentDate || '').slice(0, 10);
      const theirDate = (row.csvPaymentDate || '').slice(0, 10);
      return !!ourDate && !!theirDate && ourDate !== theirDate;
    },
    statusText(row) {
      if (this.isSkipped(row)) return this.runText('paymentsReview.dateFormat.skippedRow');
      const reason = row?.mismatchReason;
      if (!reason) return this.runText('paymentsReview.status.willBeApplied');
      if (!REASONS.includes(reason)) return this.runText('paymentsReview.status.unknownReason');
      // "Settle them by hand" names no installments and cannot be acted on, so the dates the
      // backend worked out are part of the sentence rather than something to hunt for.
      const covered = Array.isArray(row.coveredDueDates) ? row.coveredDueDates : [];
      return this.runText(`paymentsReview.mismatchReason.${reason}`, {
        count: covered.length || row.candidateCount || 0,
        dates: covered.map((d) => this.formatDate(d)).join(', '),
      });
    },
    // The earlier debts themselves. Two at most in the line: a policy owing six is a conversation
    // with the client, not a cell in a table, and the row links to the policy for that.
    earlierUnpaidLabel(row) {
      const earlier = Array.isArray(row?.earlierUnpaid) ? row.earlierUnpaid : [];
      if (!earlier.length) return '';
      const shown = earlier.slice(0, 2)
        .map((r) => `${this.formatDate(r.dueDate)} · ${this.formatMoney(r.amount)}`)
        .join('; ');
      const rest = earlier.length - 2;
      return rest > 0
        ? this.runText('paymentsReview.earlierUnpaid.someMore', {shown, count: rest})
        : this.runText('paymentsReview.earlierUnpaid.all', {shown});
    },
    // What an already-paid receipt records as its collection, under the scheduled figures in the IN
    // RENOVA cell -- or that it records nothing yet, which is what applying the row will fix. The
    // file's side sits in the next cell, so only the recorded part is said here, amount then date
    // like every other cell.
    recordedEvidenceLabel(row) {
      const reason = row?.mismatchReason;
      if (reason !== 'ALREADY_PAID_NO_EVIDENCE' && reason !== 'ALREADY_PAID_OTHER_EVIDENCE') return '';
      if (!row.recordedPaidDate) return this.runText('paymentsReview.recordedEvidence.none');
      const recorded = `${this.formatMoney(row.recordedPaidAmount)} · ${this.formatDate(row.recordedPaidDate)}`;
      return this.runText('paymentsReview.recordedEvidence.other', {recorded});
    },
    dateTitle(row) {
      const csv = this.formatDate(row.csvPaymentDate);
      const ren = row.paymentDate ? this.formatDate(row.paymentDate) : '';
      return ren
        ? this.runText('paymentsReview.titles.csvToRenova', {csv, ren})
        : this.runText('paymentsReview.titles.csvOnly', {csv});
    },
    useSuggestedFormat() {
      const suggested = this.dateFormatCheck?.suggested;
      if (!suggested) return;
      this.$emit('changeDateFormat', suggested);
    },
    skipUnparseableRows() {
      if (!this.unparseableRowsComplete) return;
      this.skippedRowNumbers = this.unparseableRowNumbers.slice();
      this.dateFormatSkipAccepted = true;
    },
    downloadUnparseableRows() {
      this.$emit('exportRows', {
        exportRows: this.unparseableRowNumbers.slice(),
        sectionKey: 'unreadable_dates',
      });
    },
    // selection helpers -- all of them read and write the operator's decisions, nothing else
    isSelected(row) {
      if (!this.rowIsSelectable(row)) return false;
      const decision = this.decisionOf(row);
      if (decision.intent === 'SKIP') return false;
      return (!this.nameRows[row.rowNumber] || !!decision.sameClient)
        && (!this.writeOffRows[row.rowNumber] || !!decision.writeOff);
    },
    // What ticking a row also says: "same client" on a name mismatch, "write it off" on a paid payment.
    consentOf(row) {
      return {
        ...(this.nameRows[row.rowNumber] ? {sameClient: true} : {}),
        ...(this.writeOffRows[row.rowNumber] ? {writeOff: true} : {}),
      };
    },
    // Checking is agreeing to apply; on a row whose holder name disagrees it is also saying "same client". The
    // payment the row is pointed at stays as it was. Nothing is re-checked until Apply.
    toggleSelect(row) {
      if (!this.showsCheckbox(row) || this.isBlocked(row)) return;
      if (this.isChecked(row)) {
        this.decide([row.rowNumber], {intent: 'SKIP'});
      } else {
        this.decide([row.rowNumber], {intent: 'APPLY', ...this.consentOf(row)});
      }
    },
    download(groupKey) {
      const exportRows = (this.groupsByKey[groupKey]?.rows || [])
        .map((r) => r.rowNumber)
        .filter((n) => Number.isInteger(n))
        // The file's order, not the screen's: a CSV is read next to the original.
        .sort((a, b) => a - b);
      this.$emit('exportRows', {exportRows, sectionKey: groupKey});
    },
    emitUpdate() {
      // Exactly the decisions the rows on screen were computed from, so the commit writes what the
      // screen showed.
      this.$emit('update', {
        rowDecisions: JSON.parse(this.previewedWire || '[]'),
        applyingRowNumbers: this.committableRows.map((row) => row.rowNumber),
      });
    },
    // A cancellation run says "Already canceled in Renova" where a settlement run says "Already
    // paid", and so on down the headings. Only the lines that genuinely differ are overridden --
    // anything without a `cancelRun.` twin falls back to the ordinary wording, so a heading added
    // later reads correctly on both runs instead of rendering as a raw key on one of them.
    // The values go through too: the summary sentence and legend are counts, and a key without its
    // params renders as a literal "{count}".
    // Every string of this review, in both components, is read through here -- never with $t directly, or
    // a cancel run shows the payment run's words ("Mark as paid") wherever someone forgot the override.
    // Two axes, not one. A cancel run overrides the words; so does a carrier portal read, which
    // arrives with the same rows and none of the file's vocabulary ("payments in the file" for a
    // screen where there is no file). Both are live at once -- the carrier's CANCEL_POLICY group
    // is a cancel run -- so the most specific key wins and each falls back to the next.
    runText(key, params) {
      const keys = this.textKeys(key);
      const found = keys.find((k) => k !== key && this.$te(k));
      return this.$t(found || key, params);
    },
    // Most specific first. A prefix with no twin for this key costs nothing, which is what lets
    // a string be overridden for one host by adding one key and leaving every other alone.
    textKeys(key) {
      if (!key.startsWith('paymentsReview.')) return [key];
      const rest = key.slice('paymentsReview.'.length);
      const host = this.sourcePrefix ? `paymentsReview.${this.sourcePrefix}.` : null;
      return [
        host && this.isCancelRun ? `${host}cancelRun.${rest}` : null,
        host ? `${host}${rest}` : null,
        this.isCancelRun ? `paymentsReview.cancelRun.${rest}` : null,
        key,
      ].filter(Boolean);
    },
    openPolicy(policyId) {
      this.openPolicyId = policyId;
    },
    closePolicy() {
      this.openPolicyId = null;
      this.closeChooser();
      // BaseSideModal frees the page scroll in `beforeDestroy` -- correct when it is the only modal
      // on screen, wrong here, because the review is still open behind it and the page would start
      // scrolling under it. Re-asserted rather than worked around in the component, which a dozen
      // other screens depend on behaving exactly as it does.
      this.$nextTick(() => {
        document.body.style.overflow = 'hidden';
        document.body.classList.add('modal-open');
      });
    },
    // -- the chooser -------------------------------------------------------------------------
    keyOf(row) {
      return row && Number.isInteger(row.rowNumber) ? `${row.policyId}:${row.rowNumber}` : null;
    },
    createsFor(row) {
      return !!this.decisionOf(row).target?.create;
    },
    // "None of these" -- add a payment from the row instead of settling one. Selects the row for the
    // same reason picking a payment does: choosing IS agreeing to apply it, and asking the operator
    // to then tick the row is a second confirmation of one decision.
    chooseCreate(row) {
      if (!row) return;
      this.unblock(row);
      this.decide([row.rowNumber], {intent: 'APPLY', target: {create: true}});
      this.closeChooser();
      this.recheckNow();
    },
    chosenIdFor(row) {
      return this.decisionOf(row).target?.installmentId || null;
    },
    // What this row will settle as things stand: the operator's choice if they made one, ours
    // otherwise. The chooser opens pre-selected on it.
    targetIdFor(row) {
      return this.chosenIdFor(row) || row?.installmentId || null;
    },
    canChoose(row) {
      if (!this.allowChooser) return false;
      // Already paid: there is no other payment for this row to be, and nothing to write to one.
      if (DONE_REASONS.has(row?.mismatchReason)) return false;
      return !!(row && row.policyId && this.schedulesByPolicy[row.policyId]);
    },
    openChooser(row) {
      if (!this.canChoose(row)) return;
      const key = this.keyOf(row);
      this.choosingKey = this.choosingKey === key ? null : key;
      // A row already split opens on its split, ticked as it was left.
      const ids = this.splitIdsFor(row);
      this.splitDraft = this.choosingKey && ids.length >= 2 ? ids.slice() : null;
    },
    closeChooser() {
      this.choosingKey = null;
      this.splitDraft = null;
    },
    // The operator points the row at a receipt. Two things follow, and both matter:
    //
    // the row is selected -- picking a receipt IS agreeing to apply it, and making them then tick
    // the row as well would be a second confirmation of the same decision; and the preview is
    // re-run, because re-pointing one row changes the verdict on others. A row moved off a receipt
    // stops contesting it with the row beside it, and the debt this row leaves behind is recomputed
    // against whatever it now settles. Neither can be worked out here.
    chooseReceipt(row, receiptId) {
      if (!row || !receiptId) return;
      this.unblock(row);
      this.decide([row.rowNumber], {intent: 'APPLY', target: {installmentId: receiptId}});
      this.closeChooser();
      this.recheckNow();
    },
    // A payment this row may be pointed at: any unpaid one. One another row holds stays pickable, with
    // the warning -- moving a row onto it is how a collision is resolved, and refusing it made the
    // collision impossible to fix from here.
    canPick(row, receipt) {
      return !!(row && receipt && receipt.status === 'UNPAID');
    },
    receiptIsClaimedByAnother(row, receipt) {
      const claims = receipt?.claimedByRows || [];
      return claims.some((n) => n !== row.rowNumber);
    },
    // Named by what the operator can see on screen: the file line, and that row's own amount and date.
    receiptClaimLabel(row, receipt) {
      const others = (receipt?.claimedByRows || []).filter((n) => n !== row.rowNumber);
      if (!others.length) return '';
      return this.runText('paymentsReview.chooser.claimedBy', {
        rows: others.map((n) => {
          const other = this.rowByNumber.get(n);
          const line = n + this.fileRowOffset;
          return other
            ? `${line} (${this.formatMoney(other.csvPaymentAmount)} · ${this.formatDate(other.csvPaymentDate)})`
            : String(line);
        }).join(', '),
      });
    },
    // -- the chooser as the policy page's table, and its split ---------------------------------
    splitIdsFor(row) {
      return this.decisionOf(row).target?.installmentIds || [];
    },
    // Split is for one collection that paid several payments. A cancellation run collects nothing, and
    // a split needs two payments it can close.
    canSplit(row) {
      if (!row || this.isCancelRun) return false;
      return this.chooserReceipts.filter((receipt) => this.canPick(row, receipt)).length >= 2;
    },
    toggleSplitMode() {
      const row = this.chooserRow;
      if (!row) return;
      if (this.splitDraft) {
        this.splitDraft = null;
        return;
      }
      // Start from the payment the row is on now, when it can be part of a split.
      const target = this.chooserReceipts.find((r) => r.id === this.targetIdFor(row));
      this.splitDraft = target && this.canPick(row, target) ? [target.id] : [];
    },
    receiptIsPicked(row, receipt) {
      if (this.splitDraft) return this.splitDraft.includes(receipt.id);
      return receipt.id === this.targetIdFor(row);
    },
    // A click on a payment: ticks it in a split, or points the row at it.
    pickReceipt(row, receipt) {
      if (!this.canPick(row, receipt)) return;
      if (!this.splitDraft) {
        this.chooseReceipt(row, receipt.id);
        return;
      }
      this.splitDraft = this.splitDraft.includes(receipt.id)
        ? this.splitDraft.filter((id) => id !== receipt.id)
        : [...this.splitDraft, receipt.id];
    },
    // The ticked payments, latest last -- the order the backend settles them in.
    splitDraftReceipts() {
      const ids = new Set(this.splitDraft || []);
      return this.chooserReceipts.filter((r) => ids.has(r.id));
    },
    chooseSplit(row) {
      const ids = this.splitDraftReceipts().map((r) => r.id);
      if (!row || ids.length < 2) return;
      this.unblock(row);
      this.decide([row.rowNumber], {intent: 'APPLY', target: {installmentIds: ids}});
      this.closeChooser();
      this.recheckNow();
    },
    splitSummary(row) {
      const ticked = this.splitDraftReceipts();
      if (ticked.length < 2) return this.runText('paymentsReview.chooser.splitPick');
      return this.runText('paymentsReview.chooser.splitSummary', {
        date: this.formatDate(ticked[ticked.length - 1].dueDate),
        amount: this.moneyWithCurrency(row.csvPaymentAmount, row),
        count: ticked.length - 1,
      });
    },
    moneyWithCurrency(val, row) {
      const code = this.schedulesByPolicy[row?.policyId]?.currencyCode;
      const money = this.formatMoney(val);
      return code && money !== '—' ? `${money} ${code}` : money;
    },
    // The policy page's Days column: signed days late for a paid payment, days overdue for one owed.
    receiptDays(receipt) {
      const due = this.parseDate(receipt.dueDate);
      const paid = this.parseDate(receipt.paidDate);
      const daysLate = receipt.status === 'PAID' && due !== null && paid !== null
        ? Math.round((paid - due) / 86400000)
        : undefined;
      return this.$options.filters.daysOffSchedule({daysLate, daysOverdue: receipt.daysOverdue});
    },
    statusColor(receipt) {
      return PAYMENT_STATUSES_COLORS[receipt.computedStatus] || PAYMENT_STATUSES_COLORS[PAYMENT_STATUS.UPCOMING];
    },
    statusLabel(receipt) {
      return PAYMENT_STATUSES_TEXTS[receipt.computedStatus] || PAYMENT_STATUSES_TEXTS[PAYMENT_STATUS.UNDEFINED];
    },
    // The chooser's last column: what a ticked payment becomes in a split, who else has a payment, or
    // which one we picked.
    receiptNote(row, receipt) {
      if (this.splitDraft && this.splitDraft.includes(receipt.id)) {
        const ticked = this.splitDraftReceipts();
        const latest = ticked.length >= 2 && ticked[ticked.length - 1].id === receipt.id;
        return this.runText('paymentsReview.chooser.splitWillBe', {
          amount: latest ? this.moneyWithCurrency(row.csvPaymentAmount, row) : this.moneyWithCurrency(0, row),
          date: this.formatDate(row.csvPaymentDate),
        });
      }
      if (this.receiptIsClaimedByAnother(row, receipt)) return this.receiptClaimLabel(row, receipt);
      if (receipt.id === this.chosenIdFor(row) || this.splitIdsFor(row).includes(receipt.id)) {
        return this.runText('paymentsReview.rowState.chosen');
      }
      if (receipt.id === row.suggestedInstallmentId) return this.runText('paymentsReview.rowState.suggested');
      return '';
    },
    chooserRowClass(receipt) {
      const row = this.chooserRow;
      if (!row) return '';
      if (this.receiptIsPicked(row, receipt)) return 'chooser-row--picked';
      return this.canPick(row, receipt) ? 'chooser-row--open' : 'chooser-row--taken';
    },
    // -- cancellation runs: what a row becomes, and what follows it ---------------------------------
    receiptOf(row, id) {
      if (!row || !id) return null;
      return (this.schedulesByPolicy[row.policyId]?.receipts || []).find((r) => r.id === id) || null;
    },
    // The payment as it will stand once this row is written: the file's amount and date -- paid, or canceled.
    afterLabel(row) {
      if (this.isBlocked(row)) return '—';
      const target = this.decisionOf(row).target || {};
      const amount = this.moneyWithCurrency(row.csvPaymentAmount, row);
      const date = this.formatDate(row.csvPaymentDate);
      if (target.create || row.mismatchReason === 'WILL_CREATE_RECEIPT') {
        return this.runText(this.isCancelRun ? 'paymentsReview.after.created' : 'paymentsReview.after.added', {amount, date});
      }
      const receipt = this.receiptOf(row, this.targetIdFor(row));
      if (!receipt) return '—';
      // Already paid: nothing is written, so what the payment will stand as is what it is now --
      // paid, with whatever it records -- not the file's figures, which this row does not apply.
      if (row.mismatchReason === 'ALREADY_PAID_OTHER_EVIDENCE') {
        return this.runText('paymentsReview.after.paid', {
          amount: this.moneyWithCurrency(row.recordedPaidAmount, row),
          date: this.formatDate(row.recordedPaidDate),
        });
      }
      if (row.mismatchReason === 'ALREADY_PAID_NO_EVIDENCE') {
        return this.runText('paymentsReview.after.paidUnrecorded', {
          amount: this.moneyWithCurrency(row.paymentAmount, row),
        });
      }
      if (!this.isCancelRun) return this.runText('paymentsReview.after.paid', {amount, date});
      return this.runText('paymentsReview.after.canceled', {amount, due: this.formatDate(receipt.dueDate), date});
    },
    // A paid payment being canceled: the collection it had, which canceling writes off.
    afterNote(row) {
      if (!this.isCancelRun) return '';
      const receipt = this.receiptOf(row, this.targetIdFor(row));
      if (!receipt || receipt.status !== 'PAID') return '';
      return this.runText('paymentsReview.after.writtenOff', {date: this.formatDate(receipt.paidDate)});
    },
    // The policy's unpaid payments after the one this row cancels -- what "cancel what follows" would add.
    followingOf(row) {
      const target = this.receiptOf(row, this.targetIdFor(row));
      if (!target) return {count: 0, amount: 0};
      const later = (this.schedulesByPolicy[row.policyId]?.receipts || [])
        .filter((r) => r.status === 'UNPAID' && r.id !== target.id && String(r.dueDate) > String(target.dueDate));
      return {count: later.length, amount: later.reduce((sum, r) => sum + Number(r.amount || 0), 0)};
    },
    // Only where there is something after it, on a single payment. On a policy's last payment the switch
    // is not shown at all -- offering a choice where there is nothing to choose is a line to read for nothing.
    canFollow(row) {
      if (!this.isCancelRun || !this.rowIsSelectable(row) || this.isBlocked(row)) return false;
      const target = this.decisionOf(row).target || {};
      if (target.create || (Array.isArray(target.installmentIds) && target.installmentIds.length >= 2)) return false;
      return this.followingOf(row).count > 0;
    },
    followsOn(row) {
      return !!this.decisionOf(row).cancelFollowing;
    },
    toggleFollowing(row) {
      if (!this.canFollow(row)) return;
      this.decide([row.rowNumber], {cancelFollowing: !this.followsOn(row)});
    },
    followableRows(group) {
      return this.selectableRows(group).filter((row) => this.canFollow(row));
    },
    includeFollowing(group) {
      const rowNumbers = this.followableRows(group).map((row) => row.rowNumber);
      if (!rowNumbers.length) return;
      this.decide(rowNumbers, {cancelFollowing: true});
    },
    // What is left owing if this row settles the receipt it is pointed at. The same sentence as the
    // row's badge and the result file's column, computed in one place.
    chooserDebtLabel(row) {
      // Payments this row closes -- the one it settles and, on a split, the earlier ones -- are not
      // debts it leaves behind.
      const closing = new Set([this.targetIdFor(row), ...this.splitIdsFor(row)].filter(Boolean));
      const receipts = this.chooserReceipts;
      const latest = receipts.filter((r) => closing.has(r.id)).map((r) => String(r.dueDate)).sort().pop();
      if (!latest) return '';
      const owing = receipts
        .filter((r) => r.status === 'UNPAID' && !closing.has(r.id) && String(r.dueDate) < latest);
      if (!owing.length) return '';
      const total = owing.reduce((acc, r) => acc + Number(r.amount || 0), 0);
      return this.runText('paymentsReview.chooser.stillOwed', {
        dates: owing.map((r) => this.formatDate(r.dueDate)).join(' · '),
        total: this.formatMoney(total),
      });
    },
  },
};
</script>

<style lang="scss">
.review-content {
  position: relative;
  padding-right: 8px;
}

/* Covers the review while a re-preview runs. The box is sticky so it stays in view however far down
   the operator was when they picked -- the pick is usually made deep in a long group. */
.review-loader {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.65);
  cursor: progress;

  &__box {
    position: sticky;
    top: 40vh;
    display: flex;
    align-items: center;
    gap: 12px;
    width: fit-content;
    margin: 120px auto 0;
    padding: 12px 20px;
    border-radius: 8px;
    background: #FFFFFF;
    box-shadow: 0 8px 24px rgba(25, 18, 69, 0.15);
  }
}

.date-format-alert {
  background: #FFF8E1;
  border-left: 4px solid #F59E0B;
  padding: 16px;
  border-radius: 4px;
}

.date-format-alert__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

/* A meta group's heading -- the top-level divisions of the review. Uppercase and ruled so it reads as
   a divider over the groups rather than as one more group title. */
.review-meta {
  margin: 32px 0 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #E2E0EE;

  &__row {
    display: flex;
    align-items: baseline;
    gap: 16px;
  }

  &__title {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: #4B4866;
  }

  &__total {
    font-variant-numeric: tabular-nums;
  }

  &__sub {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7C7899;
  }

  &__toggle {
    margin-left: auto;
  }
}

.ml_16 {
  margin-left: 16px;
}

.review-section__actions {
  display: flex;
  gap: 12px;
}

.review-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 8px 0;
}

.review-section__title {
  display: flex;
  align-items: center;
  gap: 8px;

  .h4 {
    font-weight: 700;
  }
}

.title-text {
  font-weight: 700;
}

.count {
  color: var(--dark-purple);
}

.link {
  color: var(--purple);
  background: transparent;
  border: 0;
  cursor: pointer;
  margin-left: 16px;
}

.badge {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.table {
  width: 100%;
  margin-top: 8px;
}

.table__head, .table__row {
  display: grid;
  grid-template-columns: 150px 250px 190px 190px 200px 220px;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}

.table__head {
  color: var(--dark-purple);
  font-weight: 600;
}

.table__row {
  border-bottom: 1px solid var(--solid-lavender-grey);
}

.col--num {
  color: var(--dark-purple);
}

.value--muted {
  color: var(--dark-purple);
  opacity: 0.8;
  margin-left: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}

.dot--success {
  background: var(--solid-green);
}

.dot--warning {
  background: var(--solid-tangerine);
}

.dot--danger {
  background: var(--solid-tangerine);
}

.section-footer {
  margin-top: 12px;
  margin-bottom: 8px;
  display: flex;
  gap: 12px;
}

.review-section__table {
  /* The columns are fixed widths that add up to more than a narrow laptop's drawer. Scroll the table
     rather than letting it push the modal's own layout out of shape. */
  overflow-x: auto;

  .base-table td {
    padding: 8px;
    min-height: 40px;
  }

  .base-table th {
    padding-left: 8px;
    padding-right: 8px;
    box-sizing: border-box;
  }
}

.review-section__table-cell {
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* What a receipt says now, on a row that is about to change it: struck through, so the eye does not
   read it as the value that will stand. Shared with the import-history run table on purpose -- the two
   screens describe the same kind of change and should look like it. */
.run-diff--was {
  color: var(--dark-purple);
  text-decoration: line-through;
}

/* What it becomes. Bold, not green: green read as "good", and a changed value is not a verdict. */
.run-diff--now {
  color: var(--solid-black-purple);
  font-weight: 700;
}

/* The second line of a cell that carries a value and the value it replaces. */
.run-diff__second-line {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.25;
}

/* A cell holding two facts rather than one -- our spelling of a name and the file's -- stacks them
   instead of ellipsing the second away. */
.review-section__table-cell--stacked {
  display: block;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

/* The reason a row will not settle is a sentence, not a label. Let it use two lines rather than
   ellipsing away the half that says what to do about it. */
.review-section__table-cell--wrap {
  align-items: flex-start;
  white-space: normal;
  overflow: visible;
  line-height: 1.35;

  .dot {
    flex: 0 0 auto;
    margin-top: 6px;
  }
}

.action-none {
  color: var(--dark-purple);
  opacity: 0.6;
}

/* The checkbox, and the red mark beside it when the row cannot apply. */
.review-select {
  gap: 8px;
  overflow: visible;
}

.review-blocked-tooltip {
  display: inline-flex;
}

.review-blocked-icon {
  display: inline-flex;
  color: #A32C2C;
  cursor: default;
}

/* A blocked row is marked along its left edge, so a long table can be scanned for them. */
.review-row--blocked td:first-child {
  box-shadow: inset 3px 0 0 #A32C2C;
}

/* A value in the file that differs from the payment. */
.review-diff--changed {
  color: #A32C2C;
  font-weight: 600;
}

/* A second fact under a cell's first: a holder name, a refused pick. */
.review-subline {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.25;
  color: var(--dark-purple);
}

.review-subline.review-diff--changed {
  color: #A32C2C;
}

.review-muted {
  color: var(--dark-purple);
  opacity: 0.8;
}

/* Small, and allowed to wrap to "Change / payment" -- the action column is narrow on purpose. */
.review-change-payment {
  width: min-content;
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
}

.review-notice {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: var(--solid-light-purple);
  color: var(--solid-black-purple);
  font-size: 13.5px;
  line-height: 1.5;
}

/* Finishing a group is worth saying. The drawer's settled vocabulary -- the green disc, the ✓, the
   quiet hint under the title -- so the two screens agree about what "done" looks like. Left-aligned
   with the house's in-context padding rather than a centred illustration: the review is not empty
   because something is missing, it is empty because the work is over. */
.review-done {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  align-items: start;
  column-gap: 14px;
  padding: 24px 0;

  &__glyph {
    grid-row: 1 / span 3;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    font-size: 14px;
    background: rgba(88, 219, 77, 0.16);
    color: #2e7d32;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.012em;
    line-height: 1.35;
    color: var(--solid-black-purple);
  }

  /* The batch's own line, promoted out of the footnote it used to be. */
  &__result {
    margin-top: 4px;
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--solid-black-purple-70);
    font-variant-numeric: tabular-nums;
  }

  &__hint {
    margin-top: 4px;
    font-size: 12.5px;
    line-height: 1.45;
    color: var(--solid-dark-purple);
  }
}

.review-description {
  ol, ul {
    margin: 8px 0 8px 0;
    padding-left: 20px;
  }
}

/* The summary: one sentence and one bar, above five headings. Between them they are the whole
   answer to "how much work is this file", which is the question the screen opens with. */
.review-summary {
  &__lead {
    line-height: 1.5;
  }
}

.review-bar {
  display: flex;
  width: 100%;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: #ECECF2;

  &__seg {
    display: block;
    height: 100%;

    /* Two greens, and which is which matters. Deep green is money that is already settled in
       Renova -- finished, banked, nothing owing. Light green is what this run is about to settle:
       the same colour family because the outcome is the same, lighter because it has not happened
       yet. Grey stays for the rows nothing touches. */
    &--done { background: #12694A; }
    &--ready { background: #7BD4A8; }
    &--input { background: #E8A33D; }
    &--off { background: #D5D5E0; }
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 20px;
    margin-top: 8px;
  }
}

/* The chooser: one policy's whole schedule, drawn the way the policy page draws it -- same table,
   same columns, same dots, same formats -- so an operator can hold the two side by side and recognise
   the same rows.

   Its own frame, over the top, because the list has no bound -- a policy can carry a dozen payments
   and inline it simply ran off the end of the panel, taking the buttons that close it. */
.chooser-backdrop {
  position: fixed;
  inset: 0;
  /* Above BaseOverlay's 10000. The chooser can be opened from inside the per-policy panel, which is
     a BaseSideModal, and at any lower value it opened *behind* that panel -- the Change button
     worked, the panel appeared, and nothing was visible. */
  z-index: 10050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 24px;
  background: rgba(30, 30, 55, 0.35);
  overflow-y: auto;
}

.chooser {
  width: 100%;
  max-width: 960px;
  padding: 24px;
  border-radius: 10px;
  background: #FFFFFF;

  &__head {
    margin-bottom: 12px;
  }

  &__lead {
    margin: 0 0 8px;
  }

  &__policy {
    display: flex;
    align-items: baseline;
    gap: 16px;
    margin-bottom: 4px;
  }

  &__mode {
    margin-left: auto;
  }

  /* A schedule can be long. The table scrolls inside the frame so the buttons stay where they are. */
  &__table {
    max-height: 52vh;
    overflow-y: auto;
    margin-bottom: 12px;
  }

  /* Radio, or checkbox in a split -- drawn rather than native, so both read at the table's size. */
  &__mark {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 1.5px solid var(--solid-lavender-grey);
    border-radius: 50%;
    vertical-align: middle;
    background: #FFFFFF;

    &--box {
      border-radius: 4px;
    }

    &--on {
      border-color: var(--solid-purple);
      box-shadow: inset 0 0 0 3px #FFFFFF;
      background: var(--solid-purple);
    }

    &--box.chooser__mark--on {
      box-shadow: none;

      &::after {
        content: "";
        position: absolute;
        left: 4px;
        top: 1px;
        width: 4px;
        height: 8px;
        border: solid #FFFFFF;
        border-width: 0 2px 2px 0;
        transform: rotate(42deg);
      }
    }
  }

  /* The policy page's status cell: a dot and a word. */
  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__status-dot {
    width: 8px;
    height: 8px;
    border-radius: 8px;
  }

  /* "None of these" -- the last option, and the only one that changes a schedule's shape. Set apart
     from the table so it is reached after reading, not instead of. */
  &__option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    border-top: 1px dashed var(--solid-lavender-grey);
    cursor: pointer;

    &:hover,
    &--picked {
      background: var(--solid-lavender-ligth);
    }
  }

  &__caution {
    margin: 4px 16px 12px;
  }

  &__debt {
    margin-bottom: 12px;
    padding: 8px 12px;
    border-left: 3px solid var(--solid-orange);
  }

  &__split {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
    padding: 12px 16px;
    border-radius: 8px;
    background: var(--solid-lavender-ligth);

    p {
      flex: 1;
      margin: 0;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

/* The chooser table's rows. BaseTable scopes its own styles, so these are global and named for it. */
.chooser-row--open {
  cursor: pointer;

  &:hover td {
    background: var(--solid-lavender-ligth);
  }
}

.chooser-row--picked {
  cursor: pointer;

  td {
    background: var(--solid-lavender-ligth);
  }
}

/* Settled, or taken by another row of this file: shown, because "June is already paid" answers a
   question the operator is asking, but not selectable. */
.chooser-row--taken td {
  opacity: 0.55;
}

/* A group's note, with the one action it offers beside it. */
.review-section__note {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;

  p {
    margin: 0;
  }
}

/* The DIFFERENCE cell wraps: the chip and its help icon on the first line, where the row stands on a
   line of its own beneath them. Without the wrap the outcome was squeezed into a sliver beside the chip
   and ran over the next column. */
.review-section__table-cell--difference {
  flex-wrap: wrap;
  column-gap: 6px;
  row-gap: 4px;
}

/* Why a blocked row cannot apply, under its difference. */
.row-blocked-reason {
  display: block;
  flex: 0 0 100%;
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
  color: #A32C2C;
}

.review-updating {
  margin-right: auto;
  align-self: center;
}

/* The cancellation banner. Deliberately quiet -- it narrows an expectation rather than warning
   about a danger, and an alarm-coloured box here would read as "something is wrong". */
.review-banner {
  padding: 12px 16px;
  border-left: 3px solid #9B9BB5;
  background: #F5F5FA;
  border-radius: 4px;

  &--cancel {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border-left-color: #A32C2C;
    background: #FAE9E6;
  }

  &__icon {
    flex: 0 0 auto;
    color: #A32C2C;
  }
}

.section-footer__link {
  align-self: center;
}

/* The legend's swatches. Four bands and four numbers underneath still leaves the reader pairing
   them up by order; the swatch is the thing that makes it a legend rather than a caption. */
.review-bar {
  &__key {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &--total {
      font-weight: 600;
      color: var(--solid-black-purple);
    }
  }

  &__swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;

    &--done { background: #12694A; }
    &--ready { background: #7BD4A8; }
    &--input { background: #E8A33D; }
    &--off { background: #D5D5E0; }
  }
}

/* The DIFFERENCE column: one chip saying what differs, and nothing when nothing does. */
.diff-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  /* One difference per line: "name differs", then "+47,41", then "3 days late". */
  &__line {
    display: block;
  }

  /* Late, or over. Ordinary, and the operator is only confirming it. */
  &--neutral {
    background: #FDF3E2;
    color: #8A5A12;
  }

  /* Money that did not arrive. The one difference that leaves a debt behind, so it is the one
     worth interrupting a scan for. */
  &--short {
    background: #FCECEC;
    color: #A32C2C;
  }

  /* No payment picked yet -- not a difference so much as an unanswered question. */
  &--open {
    background: #EFEFF6;
    color: #55558A;
  }
}

/* Whose choice the row's payment is, beside the control that changes it. */
.row-state {
  font-size: 11px;
  text-transform: lowercase;
  color: #7A7A9B;
  white-space: nowrap;

  &--chosen { color: #1F9C6B; }
  &--skipped { color: #9B9BB5; }
  &--none { color: #A32C2C; }
}

.review-section {
  /* A column of its own, right-aligned: "8 / 8" and "284 / 407" are different widths, and without a
     fixed one every group title started somewhere else. Right-aligned so the numbers line up on their
     last digit and each sits against its title, as in the design's number column. */
  &__count {
    flex: 0 0 56px;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    margin-right: 8px;
  }

  /* Wraps rather than clips: on a cancel run the "cancel what follows" switch takes a line of its own
     under the tick and Change payment, where one clipped line hid the tick past the column's edge. */
  &__table-cell--action {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    overflow: visible;
    white-space: normal;
  }

  /* The file's figures are what this run writes, so they lead. */
  &__table-cell--file {
    font-weight: 600;
  }
}

.diff-chip--add {
  background: #EAF0FC;
  color: #2B4E96;
}

.row-state--create { color: #2B4E96; }

/* Policies a file mentions more than once, listed as policies. */
.policy-list {
  list-style: none;
  margin: 0;
  padding: 0;

  &__row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 8px;
    border-bottom: 1px solid #ECECF2;
    cursor: pointer;

    &:hover { background: #F5F5FA; }
  }

  &__number { width: 160px; font-variant-numeric: tabular-nums; }
  &__insured { flex: 1; }
  &__count { width: 120px; }
  &__open { margin-left: auto; }
}

/* The candidate policies behind a duplicated number, openable so the operator can go and settle
   which of them should exist. */
.candidate-list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-left: 6px;
}

/* Rows of one file fighting over one payment. Its own colour, and not the shortfall red: nothing
   is wrong with the money here, the file is simply ambiguous about which payment it means. */
.diff-chip--contested {
  background: #F3E8FB;
  color: #6B2E9E;
}

</style>
